import { MoonfallActionTrace } from '../utils/card-action-trace.js';
import { CardHover } from './card-hover.js';
import { HighlightUtil } from './board.js';
import { Cards } from './cards.js';
import { CrispHover } from './crisp-hover.js';
import { Indicators } from './indicators.js';
import { TouchPreview } from './touch-preview.js';
import { GameState } from '../game-state.js';
import { uiCssPx, uiPx, uiScale } from './ui-scale.js';
let CardDrag = {};

/**
 * CardDrag — the ONE way a card is played in a match.
 *
 * Owner decision, 2026-09-24 ("Moonfall Drag Play Brief"): every card action
 * is a drag, and dragging is the only way to play. A click or a tap never
 * plays a card; hover and tap only inspect. Two kinds of drop exist:
 *
 *  - TARGET drops land on a registered element: a targeted Spell on what it
 *    targets, a board Character on what it acts on. The element under the
 *    pointer decides (registerTarget).
 *  - ZONE drops land on a REGION: a hand Character is summoned anywhere on the
 *    battlefield above the hand, an untargeted Spell is cast the same way. The
 *    zone decides where the card settles — the slot a summon will land in —
 *    so the player sees the outcome before letting go (registerZone).
 *
 * EVERY card the player owns can be picked up ("everything is draggable").
 * A card that cannot be played right now still lifts, carries its reason
 * (`config.refusal`), lights nothing up, and comes home. Letting go anywhere
 * that does nothing — back on the hand, on empty table, Escape — flies the card
 * back to its own seat. None of this changes what the server decides: every
 * drop sends the same intent the old click did, and the server still owns
 * the answer. The presentation only observes it.
 */
CardDrag = (function () {
  'use strict';

  /* The gesture is tracked on `document`, so a second evaluation of this file
     (a duplicated script tag, a hot reload, a test re-requiring the module)
     would leave two live instances answering the same pointer — each cloning
     its own card. Retire the previous one first. */
  try { CardDrag?.destroy?.(); } catch (_) { /* nothing to retire */ }

  // --- Constants ---
  // A precision pointer starts a drag almost immediately; a fingertip does not.
  // Touch input jitters several pixels on contact, so a mouse-sized threshold
  // turns every tap into a drag that springs back.
  const THRESHOLD = 5;
  const TOUCH_THRESHOLD = 12;
  /* A finger on the ENLARGED card is first a reader: it leans the card toward
     itself and the lean follows it (card-tilt.js; asked for 2026-09-21). Only a
     deliberate carry past this distance picks the card up to play it, so
     reading a card by rubbing it can never turn into a play. */
  const INSPECTED_CARD_THRESHOLD = 28;
  const MAX_TILT = 12;
  const VELOCITY_LERP = 0.3;
  const TILT_LERP = 0.25;
  const TILT_FACTOR = 2.2;
  const SNAP_LERP = 0.25;
  const SNAP_SCALE = 0.95;
  /* THE CARRY. A card held off the table must read as HELD. Hand-sized,
     upright and shadowless it read as "a sticker sliding on the table" to a
     blind critic set against Hearthstone's carry (2026-09-25). So it is carried
     larger than it rests, above the pointer — a finger or a cursor never hides
     the card it holds — leaning into its travel on a spring, over its own
     shadow on the table. Over its own hand it settles back to its resting
     size: letting go there puts it home, and the card says so first. */
  /* Carried 1.75x its resting size, but never taller than this share of the
     screen: at 1.55x a blind critic read the card as "barely bigger than a
     board slot" (2026-09-25), and on a phone the cap keeps the carried card
     from covering the table it is being played onto. */
  const CARRY_SCALE = 1.75;
  const CARRY_MAX_VIEWPORT_SHARE = 0.38;
  const HOME_SCALE = 1;
  // How far above the pointer the carried card's centre rides, in carried heights.
  const CARRY_RAISE_MOUSE = 0.22;
  const CARRY_RAISE_TOUCH = 0.3;
  // A zone that would play the card on release draws it a touch larger — and
  // the moment it starts to, the card pulses once: "no moment says let go now"
  // (blind critic, 2026-09-25).
  const ARMED_BOOST = 1.06;
  const ARM_PULSE = 0.08;
  // The cast's wind-up swells the card this much before the slam.
  const CONSUME_WINDUP_SCALE = 1.18;
  // A card is 12.29 card-units wide (cards.css), the unit every part is drawn in.
  const CARD_WIDTH_UNITS = 12.29;
  const ARM_PULSE_MS = 220;
  /* Held still, the carried card breathes — a slow bob and sway — instead of
     freezing in the air ("it feels pinned, not held", blind critic,
     2026-09-25). It stops the frame the pointer moves again. */
  const IDLE_AFTER_MS = 250;
  const IDLE_FADE_MS = 300;
  const IDLE_BOB_PX = 2;
  const IDLE_SWAY_DEG = 1.2;
  const IDLE_HZ = 1.1;
  // Size and height changes while carried: quick, never a snap.
  const SCALE_TAU_MS = 70;
  /* THE LIFT. The card leaves the hand from exactly where it was painted — the
     hover enlargement on a desktop, the inspection popup on a phone, or its
     seat in the fan — and eases into the carry. A lift that finished in two
     frames read as the enlarged card being swapped for a small one; this one
     is a beat the eye can follow. Time-based, so every refresh rate lifts alike. */
  const LIFT_MS = 220;
  /* ...counted in painted frames, not only in time: the first frames of a
     drag stall while the carried copy is built, and a clock-driven lift spent
     a third of its shrink between two painted frames. A frame advances the
     lift by at most this much. */
  const LIFT_FRAME_MAX_MS = 25;
  // The lean: degrees per px/s of sideways travel, capped, on a spring that
  // trails the motion and swings a little past upright when the pointer stops.
  const LEAN_PER_PX_S = 0.013;
  const LEAN_MAX = 12;
  const LEAN_STIFFNESS = 320;
  const LEAN_DAMPING = 22;
  // The pickup itself swings the card: a kick of lean toward where the pointer
  // is heading, which the spring returns to upright (a peak of about 6°).
  const LIFT_KICK_DEG_S = 240;
  // A card lifted out of the hand rises clear of it, this far above its top.
  const HAND_CLEARANCE_PX = 6;
  /* The shadow the card throws on the table. Light from the upper right, like
     the rim light, so it falls down and to the left, further the higher the
     card is held: carried (1), over its hand (0.45), hanging over its landing
     slot (0.6), seated (0). A card-shaped dark set clear of the card, down
     and to the left, a little smaller than the card, as Hearthstone's is:
     faint, a blind critic could not find it on the wood ("no drop shadow can
     be read"); tucked close under the card, the card hid it ("hugging the
     card"). Set aside, it read as height — and set down as well as aside, its
     own bottom corner shows, so it reads as a card, not "a pillar"
     (2026-09-25). */
  const SHADOW_DX = -0.55;  // of the carried width, at full height
  const SHADOW_DY = 0.45;   // of the carried height, at full height
  const SHADOW_ALPHA = 0.42;
  const LIFT_CARRIED = 1;
  const LIFT_HOME = 0.45;
  const LIFT_HELD = 0.6;
  /* A zone's magnet (a summon over its landing slot): the card glides to where
     it will land rather than jumping. */
  const MAGNET_TAU_MS = 60;
  /* The pull itself is a critically damped spring that starts from the card's
     own speed: an exponential ease took its biggest step on the first frame
     and the card visibly jumped into its slot or seat (80px in one frame). */
  const MAGNET_STIFFNESS = 420;
  const MAGNET_DAMPING = 41;
  const LIFT_KICK_AT = 0.45;   // of the lift
  // The landing slot brightens as the pointer nears it, over this many of its heights.
  const NEAR_REACH_HEIGHTS = 3;
  /* Held over the landing slot while the server answers: larger than the card
     that will land and lifted off the table — the wind-up of the slam, and
     the landing's own approach pose, so the landing (fx.js) can take the card
     from exactly here. */
  const HOLD_SCALE = 1.16;
  const HOLD_RAISE = 0.2; // of the card's height
  /* Measured in the distance it covers, and seen to cover it: a card let go
     across the table eases out, arcs over it and eases in over its slot. A
     fast-start glide crossed most of the board inside one frame of film and
     then crept the last few pixels — "a teleport, then a long dead hold"
     (blind critic, 2026-09-25). */
  const HOLD_GLIDE_MIN_MS = 180;
  const HOLD_GLIDE_MAX_MS = 280;
  const HOLD_GLIDE_MS_PER_PX = 0.12;
  const HOLD_GLIDE_ARC = 0.1;  // of the distance, capped at a quarter card height
  /* Still waiting on the server once it is over its slot, the card does not
     hang dead ("reads as waiting on the server", blind critic, 2026-09-25): it
     gathers itself for the slam, a little larger and higher, easing out. Kept
     well inside the reach the landing takes it from without a glide. */
  const HOLD_WINDUP_MS = 420;
  const HOLD_WINDUP_SCALE = 1.06;
  const HOLD_WINDUP_RISE = 0.05; // of the card's height
  /* A summon whose server acceptance never produced a landing (a Stellar card
     plays its own cinematic; a snapshot the FX layer did not animate) must not
     leave its card hanging in the air. */
  const HOLD_UNCLAIMED_MS = 2600;
  const HOLD_CLAIM_GRACE_MS = 450;
  /* A cold summon's seated copy waits for its art (Board's presentation gate)
     before the landing can take the clone over. Letting the clone go before
     then put the card on the board twice — the clone fading over its slot,
     the seated copy shown — and then landed it a second time once the art
     arrived. The hold waits for the gate instead, up to this bound. */
  const HOLD_PENDING_ART_MAX_MS = 12000;
  // The card flying back to its seat: an arc, decelerating, a touch of overshoot.
  const RETURN_MS = 300;
  // A refused release shakes its head where it was let go before it goes home.
  const REFUSE_SHAKE_MS = 280;
  // Wide enough to be SEEN: at 7 px no blind critic noticed it (2026-09-25).
  const REFUSE_SHAKE_PX = 16;
  const RETURN_ARC = 0.2;   // of the distance home, capped at half a card height
  /* An untargeted Spell is spent where it was released: it GATHERS (a short
     rise and swell, brightening) and is then spent in light. The cast is sent
     early, under the gather; the board-wide surge that follows is the server's
     confirmation, and it starts from where the card was spent (castPointOf). */
  /* A cast is two beats and then light: it WINDS UP (swells and rises), then
     SLAMS down onto the table, smaller than it was carried, and its light
     breaks on the slam; it holds that frame, then dissolves into the light.
     "Nothing builds up before the flash" and "the card never presses into
     the table" (blind critics, 2026-09-25). */
  const CONSUME_WINDUP_MS = 160;
  const CONSUME_SLAM_MS = 90;
  const CONSUME_HOLD_MS = 60;
  const CONSUME_MS = 380;
  const CAST_FLASH_MS = 900;
  const CONSUME_DISPATCH_MS = 40;
  const CAST_POINT_TTL_MS = 6000;

  // --- Target registry ---
  const targets = new Map();

  function registerTarget(el, config) {
    if (!el) return;
    targets.set(el, config);
  }

  function unregisterTarget(el) {
    targets.delete(el);
  }

  function clearTargets() {
    targets.clear();
  }

  // --- Zone registry ---
  /**
   * A drop REGION rather than an element. Consulted only for drags whose
   * config says `dropMode: 'zone'` (a hand Character, an untargeted Spell);
   * those drags never resolve to an element target, so hovering a board card
   * or a portrait can never turn a summon into an attack.
   *
   *   contains(pointer, data) -> boolean   geometric: is the pointer in it?
   *   accepts(data) -> boolean             may THIS card be played here now?
   *   available(data) -> Element[]         what to mark as "it can go here"
   *                                        for the whole drag
   *   anchor(data) -> Element|null         where the card settles
   *   magnet(pointer, data) -> boolean     is the card pulled to the anchor?
   *   release: 'hold' | 'consume'          hold over the anchor until the
   *                                        landing claims it, or spend it
   *   onDrop(data) -> result|Promise       dispatch; a promise that resolves
   *                                        `{ ok: false }` means refused
   */
  const zones = new Map();

  function registerZone(key, config) {
    if (!key || !config) return;
    zones.set(key, config);
  }

  function unregisterZone(key) {
    zones.delete(key);
  }

  // --- Drag state ---
  let dragging = false;
  let card = null;
  let clone = null;
  let data = null;
  let activeConfig = null;
  let origRect = null;
  let startX = 0, startY = 0;
  let curX = 0, curY = 0;
  let prevX = 0, prevY = 0;
  let vx = 0, vy = 0;
  let tiltX = 0, tiltY = 0;
  let speedX = 0;              // sideways pointer speed, px/s, smoothed
  let lean = 0, leanVelocity = 0;
  // The clone's painted pose, by its CENTRE.
  let pose = null;
  let lifting = false;
  let liftFrom = null;
  let liftStartedAt = null;
  let liftElapsed = 0;
  let trackingPointerType = 'mouse';
  let dragPointerType = 'mouse';
  let overHome = false;
  let leftHome = false;        // the carried card has been away from its hand this drag
  let carryOffset = null;      // where the card rides relative to the pointer, eased when it changes
  let motionReducedForDrag = false;
  // The source wore the game's "playable" light when it was picked up.
  let playableLook = false;
  let armedAt = null;          // when a release last started to play the card
  let armedKey = null;
  let stillSince = null;       // when the pointer last moved
  let liftKickPending = false;
  let magnetVel = null;        // the pull toward a slot or the card's seat: a spring, from the card's own speed
  let seatPose = null;         // the carried hand card's own seat, where "home" draws it
  let rising = false;          // held clear above the hand, not yet riding the pointer
  let ghost = null;            // a picture of the card waiting in its landing slot
  let castRing = null;         // the mark under the pointer where a Spell would be cast
  let nearMarks = [];          // the landing slots that answer the approaching card
  let nearCache = null;        // their centres, re-read every few frames
  let lastFrameAt = null;
  let rafId = null;
  let hovered = null;          // { kind: 'element', el, cfg } | { kind: 'zone', key, cfg }
  let hoveredTarget = null;    // the element of an element hover, for legacy readers
  let refusal = null;          // { reason, message } while the card cannot be played
  let availableMarks = [];
  let armedMarks = [];
  let trackingCard = null;
  let trackingConfig = null;
  let trackingPointerId = null;
  let trackingSlop = THRESHOLD;
  let trackingPreviewPose = null;
  let validDrop = false;
  let settling = null;         // 'slide' | 'return' | 'consume' while a released clone animates
  let actionTraceId = null;
  let lastHitTestDebug = null;
  let interactionGeneration = 0;
  let held = null;             // a summon clone hanging over its landing slot
  const delayedTimers = new Set();

  function scheduleDelayed(callback, delayMs) {
    var timer = setTimeout(function () {
      delayedTimers.delete(timer);
      callback();
    }, delayMs);
    delayedTimers.add(timer);
    return timer;
  }

  function clearDelayedTimers() {
    delayedTimers.forEach(function (timer) { clearTimeout(timer); });
    delayedTimers.clear();
  }

  const rasterPreparations = new WeakMap();

  // --- Pending-action dim ---
  /* A valid drop dims its source card while the server resolves the action.
     The dim used to be cleared BY ACCIDENT: renderBoard wiped the board and
     rebuilt every card element on the next snapshot, so the inline style went
     with the node. renderBoard reconciles now — a board Character whose face is
     unchanged keeps its exact DOM node, and `canAttack`/`usedThisTurn` are
     deliberately unsigned — so a card dimmed here stays dimmed for the rest of
     the match unless this module ends what it started. The value also outranks
     `.card.used-this-turn { opacity: 0.7 }` and survives the turn boundary that
     removes that class.

     The dim is therefore OWNED: one card at a time, ended the moment the action
     it announces resolves, fails, dispatches nothing, or is cancelled before it
     was ever sent. It is deliberately not cleared by renderBoard — enemy-replay
     owns `opacity: 0` on board cards while a summon traveler glides, and a
     blanket clear there would flash those cards visible mid-animation. */
  const PENDING_ACTION_OPACITY = '0.4';
  let pendingActionDim = null;

  function applyPendingActionDim(cardEl, instanceId) {
    releasePendingActionDim(pendingActionDim, 'superseded');
    cardEl.style.opacity = PENDING_ACTION_OPACITY;
    pendingActionDim = { element: cardEl, instanceId: instanceId || null };
    return pendingActionDim;
  }

  function releasePendingActionDim(token, reason) {
    if (!token || pendingActionDim !== token) return false;
    pendingActionDim = null;
    /* Revert OUR write and nothing else. Anything that has since taken the
       card's opacity for its own presentation — an enemy-replay glide, a fade —
       still owns it, and clobbering that is the exact failure a blanket clear
       in renderBoard would have caused. */
    if (token.element.style.opacity === PENDING_ACTION_OPACITY) {
      token.element.style.opacity = '';
    }
    return true;
  }

  /** End the dim with the action it announces. */
  function bindPendingActionDim(token, result, dispatched) {
    if (!token) return;
    if (!dispatched || !result || typeof result.then !== 'function') {
      // Nothing is in flight: a handler that threw, refused (the deck targets
      // return outright while another action is in progress), or answered
      // synchronously has no settlement for the dim to wait on.
      releasePendingActionDim(token, 'no-action-in-flight');
      return;
    }
    Promise.resolve(result).then(
      function () { releasePendingActionDim(token, 'action-resolved'); },
      function () { releasePendingActionDim(token, 'action-failed'); }
    );
  }

  // --- A card played from the hand leaves the hand ---
  /* A board Character that acts stays on the board, dimmed while its action
     resolves (above). A card played FROM THE HAND is spent: it used to come
     back into the fan, dimmed, for as long as the server took — seconds, with
     a live storyteller — which read as the play not having happened. It now
     leaves the hand the moment it is played, and the rest of the fan closes
     over its seat (Cards.reflowHand). The server's answer settles it: a card
     the server spent is simply gone, a card it refused comes back to its seat
     and shakes. Presentation only; the server decides. */
  const HAND_PLAY_RESOLVE_GRACE_MS = 900;
  const pendingHandPlays = new Map();
  let latestHandPlay = null;

  function isPlayPending(instanceId) {
    return instanceId !== undefined && instanceId !== null && pendingHandPlays.has(String(instanceId));
  }

  function pendingHandNodes(instanceId) {
    const escaped = window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(String(instanceId))
      : String(instanceId).replace(/["\\]/g, '\\$&');
    return [...document.querySelectorAll(`#player-hand .card[data-instance-id="${escaped}"]`)]
      .filter(function (node) { return !node.classList.contains('card-drag-clone'); });
  }

  /* Every live node of a carried card in its own zone. A render can rebuild
     the card while it is in the air, and cards.js / board.js give the rebuilt
     node the carried look (`dragging`, hidden) — which the node the drag
     started from never passes on. Only the source's own zone: the opponent can
     hold a card with the same instance name. */
  function liveNodesOf(instanceId, sourceType) {
    if (instanceId === undefined || instanceId === null) return [];
    const escaped = window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(String(instanceId))
      : String(instanceId).replace(/["\\]/g, '\\$&');
    const zone = sourceType === 'board' ? '#player-board' : '#player-hand';
    const nodes = /** @type {HTMLElement[]} */ ([...document.querySelectorAll(`${zone} .card[data-instance-id="${escaped}"]`)]);
    return nodes.filter(function (node) { return node.isConnected && !node.classList.contains('card-drag-clone'); });
  }

  /* The carry is over: no node of the card may keep its carried look.
     `.card.dragging` is pointer-events: none, so a node that kept it was shown
     again and could never be hovered, lit or pressed — a press went to its
     neighbour (owner playtest, 2026-09-25: a cast Spell back in the hand). */
  function dropCarriedLook(instanceId, sourceType) {
    liveNodesOf(instanceId, sourceType).forEach(function (node) {
      node.classList.remove('dragging');
      if (node.style.visibility === 'hidden') node.style.visibility = '';
    });
  }

  function reflowHandWith(mutate) {
    try {
      if (typeof Cards?.reflowHand === 'function') {
        Cards.reflowHand(mutate);
        return;
      }
    } catch (_) { /* fall through to a plain change */ }
    mutate();
  }

  function beginPendingHandPlay(cardEl, instanceId, config) {
    const id = String(instanceId);
    const token = { instanceId: id, onRefused: config && config.onRefusedDrop, timer: null };
    pendingHandPlays.set(id, token);
    reflowHandWith(function () {
      cardEl.style.visibility = '';
      cardEl.style.display = 'none';
      cardEl.dataset.playPending = '1';
    });
    latestHandPlay = token;
    return token;
  }

  function endPendingHandPlay(token, { refused = false } = {}) {
    if (!token || pendingHandPlays.get(token.instanceId) !== token) return;
    pendingHandPlays.delete(token.instanceId);
    if (token.timer) clearTimeout(token.timer);
    const nodes = pendingHandNodes(token.instanceId).filter(function (node) {
      return node.dataset.playPending === '1';
    });
    if (!nodes.length) return; // spent: the server took it out of the hand
    reflowHandWith(function () {
      nodes.forEach(function (node) {
        node.style.display = '';
        delete node.dataset.playPending;
        const seat = /** @type {HTMLElement} */ (node);
        seat.classList.remove('dragging');
        if (seat.style.visibility === 'hidden') seat.style.visibility = '';
      });
    });
    if (refused && typeof token.onRefused === 'function') {
      try {
        token.onRefused({ element: nodes[0], refusal: { reason: 'server', message: '' } });
      } catch (_) { /* feedback is presentation only */ }
    }
  }

  /** Settle a hand play with the action it dispatched. */
  function bindPendingHandPlay(token, result, dispatched) {
    if (!token) return;
    if (!dispatched || !result || typeof result.then !== 'function') {
      endPendingHandPlay(token, { refused: true });
      return;
    }
    Promise.resolve(result).then(function (outcome) {
      if (outcome && outcome.ok === false) {
        endPendingHandPlay(token, { refused: true });
        return;
      }
      /* Accepted. The snapshot that takes the card out of the hand can land
         after the acknowledgement; give it a beat before deciding the card is
         still here (a stubbed storyteller leaves it, and it returns quietly). */
      token.timer = setTimeout(function () {
        endPendingHandPlay(token, { refused: false });
      }, HAND_PLAY_RESOLVE_GRACE_MS);
    }, function () {
      endPendingHandPlay(token, { refused: true });
    });
  }

  function releaseAllPendingHandPlays() {
    [...pendingHandPlays.values()].forEach(function (token) { endPendingHandPlay(token); });
  }

  /* An intent the player cancels, dismisses, or lets a turn boundary close
     sends no action at all, so nothing will ever settle the promise the drop
     handed us — the >=6-mana menu can also sit open for as long as the player
     likes while authoritative snapshots keep arriving. ActionIntent announces
     its own end; that announcement is this path's release. One-way and
     presentation-only: nothing here can change what the intent does. */
  function onActionIntentSettled() {
    releasePendingActionDim(pendingActionDim, 'action-intent-settled');
  }
  window.addEventListener('moonfall:action-intent-settled', onActionIntentSettled);

  // --- Helpers ---
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
  }

  /* Reduced motion keeps every beat of a play — the lift, the landing slot,
     the card going home — but never makes the eye follow a card travelling on
     its own: travel becomes a short fade in place. The card still follows the
     pointer; that motion is the player's. */
  function reducedMotion() {
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    } catch (_) {
      return false;
    }
  }

  function nowMs() {
    return (typeof performance !== 'undefined' && typeof performance.now === 'function')
      ? performance.now()
      : Date.now();
  }

  function traceRecordFor(traceId, eventName, details) {
    if (!traceId) return;
    MoonfallActionTrace?.record?.(traceId, eventName, details);
  }

  function traceRecord(eventName, details) {
    traceRecordFor(actionTraceId, eventName, details);
  }

  function traceFinishFor(traceId, eventName, details) {
    if (!traceId) return;
    MoonfallActionTrace?.finish?.(traceId, eventName, details);
  }

  function traceFinish(eventName, details) {
    traceFinishFor(actionTraceId, eventName, details);
  }

  function describeElement(el) {
    if (!el || !el.tagName) return null;
    return {
      tag: String(el.tagName).toLowerCase(),
      id: el.id || null,
      zone: el.dataset?.zone || null,
      instanceId: el.dataset?.instanceId || null,
      classes: typeof el.className === 'string'
        ? el.className.split(/\s+/).filter(Boolean).slice(0, 5)
        : [],
    };
  }

  function cardSize() {
    const size = Cards?.getCardPixelSize?.() || null;
    return {
      width: size && size.width > 0 ? size.width : 86,
      height: size && size.height > 0 ? size.height : 155,
    };
  }

  /**
   * Where and how large `el` is PAINTED right now, as a card-pose: its centre,
   * its uniform scale against the resting card size, and its in-plane rotation.
   * The rotation comes from the computed transform; the scale from the painted
   * box, so CSS `zoom` (board hover) and transform scale (hand hover) both read
   * true, and a fan-rotated card is not mistaken for a larger one.
   */
  function paintedPose(el) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return null;
    const rect = el.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return null;
    let rotZ = 0;
    try {
      const transform = window.getComputedStyle(el).transform;
      const Matrix = window.DOMMatrixReadOnly || window.DOMMatrix;
      if (transform && transform !== 'none' && typeof Matrix === 'function') {
        const matrix = new Matrix(transform);
        rotZ = Math.atan2(matrix.b, matrix.a) * 180 / Math.PI;
      }
    } catch (_) { rotZ = 0; }
    const size = cardSize();
    const rad = rotZ * Math.PI / 180;
    const unscaledWidth = size.width * Math.abs(Math.cos(rad)) + size.height * Math.abs(Math.sin(rad));
    const scale = unscaledWidth > 0 ? rect.width / unscaledWidth : 1;
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      scale: Number.isFinite(scale) && scale > 0 ? scale : 1,
      rotZ: Number.isFinite(rotZ) ? rotZ : 0,
      opacity: 1,
    };
  }

  function currentRefusal(config) {
    try {
      const answer = config && typeof config.refusal === 'function' ? config.refusal() : null;
      if (!answer) return null;
      if (typeof answer === 'string') return { reason: answer, message: '' };
      return { reason: answer.reason || 'refused', message: answer.message || '' };
    } catch (_) {
      return null;
    }
  }

  // --- Clone ---
  function cloneTransform(p, el) {
    /* The clone is LAID OUT at `_layoutScale` x its resting size (layoutClone),
       so the transform only ever shrinks a raster painted at full size: a
       carried card is never a small raster stretched up (blurred), and never a
       huge one squeezed down under the tilt (aliased) — owner, 2026-09-25. */
    const base = p.scale / ((el && el._layoutScale) || 1);
    // A squash (0..1) presses the card flat against the table: wider, shorter.
    const squash = Number(p.squash) || 0;
    const scale = Math.abs(squash) > 0.001
      ? `scale(${(base * (1 + 0.1 * squash)).toFixed(4)}, ${(base * (1 - 0.1 * squash)).toFixed(4)})`
      : `scale(${base.toFixed(4)})`;
    return `perspective(${uiPx(600)}px) rotateX(${tiltX.toFixed(2)}deg) rotateY(${tiltY.toFixed(2)}deg)`
      + ` rotateZ(${(p.rotZ || 0).toFixed(2)}deg) ${scale}`;
  }

  function applyPose(el, p, extra) {
    if (!el || !p) return;
    const w = el._pw || el.offsetWidth;
    const h = el._ph || el.offsetHeight;
    el.style.left = (p.x - w / 2) + 'px';
    el.style.top = (p.y - h / 2) + 'px';
    el.style.transform = extra && typeof extra.transform === 'string' ? extra.transform : cloneTransform(p, el);
    if (p.opacity !== undefined) el.style.opacity = p.opacity >= 0.999 ? '' : String(p.opacity);
    if (el._dragShadow) applyShadow(el._dragShadow, p);
  }

  // --- The shadow on the table ---
  /* A separate element, never part of the card: it stays flat on the table
     while the card leans, and it is painted once (a blurred plate) and only
     ever moved, so it costs the compositor nothing per frame. */
  function createShadow(width, height, radius) {
    /** @type {any} */
    const shadow = document.createElement('div');
    shadow.className = 'card-drag-shadow';
    shadow.setAttribute('aria-hidden', 'true');
    shadow.style.width = width + 'px';
    shadow.style.height = height + 'px';
    if (radius) shadow.style.borderRadius = radius;
    shadow._pw = width;
    shadow._ph = height;
    return shadow;
  }

  function shadowLift(p) {
    return p && Number.isFinite(p.lift) ? clamp(p.lift, 0, 1.5) : LIFT_CARRIED;
  }

  function shadowTransform(shadow, p) {
    const w = shadow._pw;
    const h = shadow._ph;
    const height = shadowLift(p);
    const x = p.x - w / 2 + SHADOW_DX * w * p.scale * height;
    const y = p.y - h / 2 + SHADOW_DY * h * p.scale * height;
    // A soft, card-shaped shadow a touch smaller than the card, never a slab.
    return `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) rotate(${(p.rotZ || 0).toFixed(2)}deg)`
      + ` scale(${(p.scale * (0.93 + 0.04 * height)).toFixed(3)})`;
  }

  function shadowOpacity(p) {
    const height = shadowLift(p);
    // Down on the table the card covers its own shadow: none is left to see.
    if (height <= 0.001) return 0;
    const cardOpacity = p.opacity === undefined ? 1 : p.opacity;
    return SHADOW_ALPHA * cardOpacity * clamp(0.3 + 0.7 * height, 0, 1);
  }

  function applyShadow(shadow, p) {
    shadow.style.transform = shadowTransform(shadow, p);
    shadow.style.opacity = shadowOpacity(p).toFixed(3);
  }

  function removeShadowOf(el) {
    const shadow = el && el._dragShadow;
    if (!shadow) return;
    el._dragShadow = null;
    shadow.remove();
  }

  function setPhase(el, phase) {
    if (el && el.dataset) el.dataset.dragPhase = phase;
  }

  function createClone(startPose) {
    // Defensive sweep: clear any orphaned clones left behind by a prior drag
    // whose cleanup was skipped (e.g. an onDrop handler threw). Without this they
    // accumulate as invisible, fixed-position "ghosts" over board slots and
    // hijack hover/preview. Safe here: a single drag runs at a time, so any clone
    // present at the start of a new drag is stale. A summon HELD over its
    // landing slot is not stale — it belongs to an action still in flight.
    var orphans = document.querySelectorAll('.card-drag-clone:not(.card-drag-held), .card-drag-shadow:not(.card-drag-shadow--held)');
    if (orphans.length) {
      console.warn('[CardDrag] Cleared ' + orphans.length + ' orphaned drag clone(s) at drag start.');
      orphans.forEach(function (el) { el.remove(); });
    }

    const el = card.cloneNode(true);
    Cards?.copyCanvasPaints?.(card, el);
    el.id = ''; // prevent duplicate DOM IDs
    el.classList.add('card-drag-clone');
    el.classList.remove(
      'hover-active', 'dragging', 'potential-target',
      'valid-target', 'legal-play', 'card-tilt-active', 'safari-hand-zoom-hover',
      'draggable', 'interactive-card', 'tutorial-allowed',
      'card-draw-in', 'fx-draw-hold', 'card-cohort-hold', 'invalid-shake'
    );
    /* The source is hidden (its seat stays empty while it is carried) BEFORE
       it is copied, so the copy starts hidden too. Show it explicitly: the
       targeted path used to un-hide it by accident in its hit test, which a
       zone drag never runs, and every summon was carried invisibly. */
    el.style.visibility = '';
    el.style.opacity = '';
    delete el.dataset.dragHeldArrival;
    // Prevent card-tilt.js MutationObserver from attaching tilt handlers
    // to the clone.  Without this, tilt rapidly toggles card-tilt-active
    // which changes transform-style (flat ↔ preserve-3d) and will-change,
    // causing visual flicker and position jumps.
    el.classList.add('card-tilt-disabled');
    // The clone is a picture of the card, never a second copy of its state.
    el.removeAttribute('aria-describedby');
    el.setAttribute('aria-hidden', 'true');
    ['--card-hover-transform', '--card-tilt-rotate-x', '--card-tilt-rotate-y'].forEach(function (name) {
      el.style.removeProperty(name);
    });

    // Ensure .card-glare exists inside clone
    if (!el.querySelector('.card-glare')) {
      const glare = document.createElement('div');
      glare.className = 'card-glare';
      el.prepend(glare);
    }

    // Clear any inherited CSS zoom (board cards use zoom for hover enlargement;
    // without this the clone inherits the zoom AND gets a scale transform,
    // double-applying the magnification).
    el.style.zoom = '';

    // Use CSS-level card dimensions — same as the card's resting size. The
    // probe (not a vmin formula) keeps the clone identical to every other card
    // once --card-unit floors on phones.
    var size = cardSize();
    var pxW = size.width;
    var pxH = size.height;

    el.style.position = 'fixed';
    el.style.zIndex = '15000';
    el.style.pointerEvents = 'none';
    el.style.margin = '0';
    el.style.overflow = 'visible';
    el.style.transformOrigin = 'center center';
    el.style.transformStyle = 'preserve-3d';
    el.style.willChange = 'left, top, transform';
    /* It leaves the hand in the light it wore there: the playable glow does
       not drop out on the press and come back as something else. */
    el._tone = restingTone();
    el.style.transition = 'box-shadow 0.2s ease, filter 0.2s ease';
    layoutClone(el, 1);

    let radius = '';
    try { radius = window.getComputedStyle(card).borderRadius || ''; } catch (_) { radius = ''; }
    const shadow = createShadow(pxW, pxH, radius);
    el._dragShadow = shadow;

    // Position via left/top so the card can't teleport to (0,0) if transform
    // is interfered with. Transform is only used for tilt/scale decoration.
    // The lift starts at the painted pose with the card still ON the table.
    pose = startPose
      ? Object.assign({ opacity: 1, lift: 0 }, startPose)
      : Object.assign({ opacity: 1 }, carryPose());

    document.body.appendChild(shadow);
    /* In the page at its resting size first, untransformed: its description's
       line breaks are read there and frozen (CrispHover), so drawing the card
       larger never tips a word onto another line. Then it is laid out at the
       largest size it will be shown, in the same task — nothing is painted in
       between. */
    el.style.transform = 'none';
    document.body.appendChild(el);
    try { CrispHover?.pinDescription?.(el); } catch (_) { /* line breaks then follow the layout */ }
    const layout = layoutScalesFor(startPose);
    el._settledLayoutScale = layout.settled;
    layoutClone(el, layout.lift);
    applyPose(el, pose);
    setPhase(el, 'lift');
    clone = el;
  }

  /**
   * The sizes the clone is LAID OUT at, as multiples of the resting card.
   * `settled`: the size it is carried at while a release would play it (the
   * armed carry), or a held summon's wind-up if that is larger. So where the
   * card spends its carry — over the table — it is drawn 1:1: a layout larger
   * than the size shown is a texture the compositor shrinks every frame, which
   * software compositing pays for in full (measured 2026-09-25: drawing the
   * carried card cost 30% more than the resting-size layout did when it was
   * laid out at its largest pose, 18% more at the armed carry). Only the arm's
   * pulse (+8%, 220ms), the lift's overshoot and the cast's wind-up swell past
   * it, briefly. `lift`: while it lifts off a painted enlargement larger than
   * that, it is laid out at the enlargement's size and re-laid out at
   * `settled` when the lift ends.
   */
  function layoutScalesFor(startPose) {
    const settled = Math.max(
      1,
      HOLD_SCALE * HOLD_WINDUP_SCALE,
      carryScale() * ARMED_BOOST,
    );
    const from = startPose && Number.isFinite(startPose.scale) ? startPose.scale : 0;
    const up = (value) => Math.ceil(value * 1000) / 1000;
    return { settled: up(settled), lift: up(Math.max(settled, from)) };
  }

  /* The clone's size, from the unit up: width, height and every part drawn in
     --card-unit / --card-width / --card-height (fonts, frame, art window, mana
     gem), exactly as the popup grows a card physically (cards.js). The shadow
     and the ghost keep the resting size; poses stay in resting units. */
  function layoutClone(el, layoutScale) {
    const rest = cardSize();
    const k = Math.max(1, Number(layoutScale) || 1);
    el._layoutScale = k;
    el._pw = rest.width * k;
    el._ph = rest.height * k;
    el.style.width = el._pw + 'px';
    el.style.height = el._ph + 'px';
    el.style.setProperty('--card-unit', `${(rest.width / CARD_WIDTH_UNITS) * k}px`);
    el.style.setProperty('--card-width', `${el._pw}px`);
    el.style.setProperty('--card-height', `${el._ph}px`);
    el.style.setProperty('--card-frame-scale', `${k}`);
    el.style.boxShadow = cloneGlow(el._tone, k);
  }

  /* The clone's own edge light: a tight contact shadow (the table shadow is
     its own element) and a rim in the colour of what a release would do. */
  function cloneGlow(tone, layoutScale = 1) {
    // Lengths live in the clone's own space; laid out larger, they are drawn larger.
    const k = Number(layoutScale) > 0 ? Number(layoutScale) : 1;
    const len = (n) => (Math.abs(k - 1) < 0.001 ? uiCssPx(n) : `calc(${k.toFixed(4)} * ${uiCssPx(n)})`);
    const contact = `0 ${len(3)} ${len(10)} rgba(0,0,0,0.38)`;
    if (tone === 'refused') return `${contact}, 0 0 0 ${len(3)} rgba(255,110,100,0.92), 0 0 ${len(20)} rgba(255,80,70,0.62)`;
    // The hand's own playable green (cards.css `.legal-play`), carried.
    if (tone === 'playable') {
      return `${contact}, 0 0 0 ${len(1.5)} rgba(90,255,120,0.8), 0 0 ${len(16)} rgba(40,255,90,0.62)`;
    }
    if (tone === 'strike') return `${contact}, 0 0 ${len(16)} rgba(255,60,60,0.55)`;
    if (tone === 'summon') {
      return `${contact}, 0 0 0 ${len(2)} rgba(255,228,160,0.9), 0 0 ${len(26)} rgba(245,206,110,0.85)`;
    }
    if (tone === 'cast') {
      return `${contact}, 0 0 0 ${len(3)} rgba(232,214,255,0.98), 0 0 ${len(38)} rgba(190,150,255,1)`;
    }
    return `${contact}, 0 0 ${len(14)} rgba(120,180,255,0.35)`;
  }

  /** The light of a carried card over nothing a release would do anything to. */
  function restingTone() {
    if (refusal) return 'refused';
    return playableLook ? 'playable' : null;
  }

  /* The one swell as a release starts to play the card. */
  function armPulse() {
    if (armedAt === null || motionReducedForDrag) return 1;
    const t = ((lastFrameAt === null ? nowMs() : lastFrameAt) - armedAt) / ARM_PULSE_MS;
    return t >= 0 && t <= 1 ? 1 + ARM_PULSE * Math.sin(Math.PI * t) : 1;
  }

  /** How much larger than it rests the card is carried, on this screen. */
  function carryScale() {
    const size = cardSize();
    const screen = Number(window.innerHeight) || 0;
    if (!(size.height > 0) || !(screen > 0)) return CARRY_SCALE;
    return clamp((screen * CARRY_MAX_VIEWPORT_SHARE) / size.height, HOME_SCALE, CARRY_SCALE);
  }

  /** Where the carried card rides for a pointer at (x, y). */
  function carryPose(x = curX, y = curY) {
    const size = cardSize();
    const raise = dragPointerType === 'touch' ? CARRY_RAISE_TOUCH : CARRY_RAISE_MOUSE;
    const scale = carryScale();
    return {
      x,
      y: y - size.height * scale * raise,
      scale,
      rotZ: 0,
      lift: LIFT_CARRIED,
    };
  }

  /* THE REASON TRAVELS WITH THE CARD. A card that cannot be played still
     lifts — the brief says everything is draggable — but it says why while it
     is held, above the card so a finger on a phone never covers it. */
  function syncRefusalChip() {
    if (!clone) return;
    let chip = clone.querySelector(':scope > .card-drag-refusal');
    clone.classList.toggle('card-drag-clone--refused', !!refusal);
    // Short of mana, the card's own mana gem says it too, words or none.
    clone.classList.toggle('card-drag-clone--refused-mana', !!refusal && refusal.reason === 'mana');
    if (!refusal || !refusal.message) {
      if (chip) chip.remove();
      return;
    }
    if (!chip) {
      chip = document.createElement('div');
      chip.className = 'card-drag-refusal';
      /* It fades in once the card has been lifted (levelRefusalChip) — not
         over an enlargement still shrinking under it, where it popped in at
         full size. */
      chip.classList.add('card-drag-refusal--waiting');
      clone.appendChild(chip);
    }
    if (chip.textContent !== refusal.message) chip.textContent = refusal.message;
  }

  /* The reason goes with the release: it fades from the moment the card is
     let go, never left hanging over the hand after the card is home ("the
     label is left behind after release", blind critic, 2026-09-25). */
  function dismissRefusalChip(el) {
    const chip = el && el.querySelector(':scope > .card-drag-refusal');
    if (chip) chip.classList.add('card-drag-refusal--leaving');
  }

  /* The reason reads level and at its own size whatever the card is doing:
     it lives on the card (it travels and shakes with it), but it takes back
     the card's lean and scale, so it never arrives at an enlarged card's size
     or tilts its words. */
  function levelRefusalChip() {
    const chip = clone && clone.querySelector(':scope > .card-drag-refusal');
    if (!chip || !pose) return;
    if (!lifting || liftElapsed >= LIFT_MS * 0.5) chip.classList.remove('card-drag-refusal--waiting');
    const scale = pose.scale > 0 ? pose.scale : 1;
    chip.style.transform = `translateX(-50%) rotateZ(${(-(pose.rotZ || 0)).toFixed(2)}deg)`
      + ` scale(${(1 / scale).toFixed(4)})`;
  }

  /* While a release would summon, a line of light runs from the carried card
     to the slot the server will seat it in (always the next free one): the
     card and its landing are one thought, wherever the player carries it. */
  let tether = null;

  function syncTether(magnetised) {
    let anchor = null;
    if (!magnetised && hovered && hovered.kind === 'zone' && hovered.cfg.release === 'hold' && !refusal) {
      try { anchor = typeof hovered.cfg.anchor === 'function' ? hovered.cfg.anchor(data) : null; } catch (_) { anchor = null; }
    }
    const rect = anchor && typeof anchor.getBoundingClientRect === 'function' ? anchor.getBoundingClientRect() : null;
    if (!rect || !(rect.width > 0) || !pose) {
      if (tether) tether.classList.remove('card-drag-tether--shown');
      return;
    }
    if (!tether) {
      tether = document.createElement('div');
      tether.className = 'card-drag-tether';
      tether.setAttribute('aria-hidden', 'true');
      document.body.appendChild(tether);
    }
    const tx = rect.left + rect.width / 2;
    const ty = rect.top + rect.height / 2;
    const length = Math.hypot(tx - pose.x, ty - pose.y);
    const angle = Math.atan2(ty - pose.y, tx - pose.x) * 180 / Math.PI;
    tether.style.width = `${length.toFixed(1)}px`;
    tether.style.transform = `translate(${pose.x.toFixed(1)}px, ${pose.y.toFixed(1)}px) rotate(${angle.toFixed(2)}deg)`;
    tether.classList.add('card-drag-tether--shown');
  }

  function removeTether() {
    if (tether) tether.remove();
    tether = null;
  }

  // --- Begin drag ---
  function settleTouchPreviewHandoff(pointerId, accepted) {
    try {
      TouchPreview?.settleDragHandoff?.(pointerId, accepted);
    } catch (_) { /* touch inspection is an optional presentation layer */ }
  }

  function markAll(elements, className) {
    const marked = [];
    (elements || []).forEach(function (el) {
      if (el && el.classList && !el.classList.contains(className)) {
        el.classList.add(className);
        marked.push(el);
      }
    });
    return marked;
  }

  function clearMarks(list, className) {
    list.forEach(function (el) { el?.classList?.remove(className); });
    return [];
  }

  /** "It can go here" for a zone drag: a summon's landing slot (a Spell marks nothing). */
  function markAvailableZones() {
    availableMarks = clearAvailableMarks();
    nearMarks = [];
    if (!data || data.dropMode !== 'zone' || refusal) return;
    zones.forEach(function (cfg) {
      try {
        if (typeof cfg.accepts === 'function' && !cfg.accepts(data)) return;
        const elements = typeof cfg.available === 'function' ? cfg.available(data) : [];
        const marked = markAll(elements, 'drop-zone-available');
        availableMarks = availableMarks.concat(marked);
        // A card lands in a HOLD zone's slot: that slot answers its approach.
        if (cfg.release === 'hold') nearMarks = nearMarks.concat(marked);
      } catch (_) { /* an affordance may never break the drag */ }
    });
  }

  function beginDrag(cardEl, config) {
    // The press may have been held across a re-render that revoked the card's
    // eligibility (the match ended, the mulligan reopened). Eligibility is
    // re-asked here, not just at pointerdown, because the gesture now outlives
    // the element's own listeners.
    if (config.canDrag && !config.canDrag()) {
      endTracking('drag-refused', 'can-drag-revoked');
      return false;
    }
    /* A match being rejoined (or a hidden tab's) takes no play: a press held
       across the drop would go out on a socket the match no longer knows
       (replay 01a17967; game-controls.css blocks the press itself). */
    const bodyClasses = document.body?.classList;
    if (bodyClasses?.contains('game-resuming') || bodyClasses?.contains('game-suspended')) {
      endTracking('drag-refused', 'match-rejoining');
      return false;
    }

    /* INPUT IS NEVER GATED ON ART. The drag used to be refused until the card's
       raster was settled and locked, so the lifted copy could never show a
       soft or half-loaded picture. The cost was a card that did not move under
       the finger: a hover enlargement starts a re-raster, and on a phone-class
       CPU (measured at 4x throttle) the card sat still for 2.4s after the
       finger started dragging it — with dragging now the only way to play,
       every play felt dead. The drag starts at once with the pixels the card
       is showing now; the settled raster is prepared alongside and handed to
       the lifted copy the moment it exists. */
    let rasterPending = null;
    const lockRaster = Cards?.lockCardRaster;
    if (typeof lockRaster === 'function') {
      let rasterLocked = false;
      try {
        rasterLocked = lockRaster(cardEl, 'drag') === true;
      } catch (_) { /* treat an unavailable immutable paint as not ready */ }

      if (!rasterLocked) {
        const prepareRaster = Cards?.prepareCardRasterForInteraction;
        if (typeof prepareRaster === 'function') {
          rasterPending = rasterPreparations.get(cardEl) || null;
          if (!rasterPending) {
            rasterPending = Promise.resolve(prepareRaster(cardEl, 'drag'))
              .catch(() => false)
              .finally(() => rasterPreparations.delete(cardEl));
            rasterPreparations.set(cardEl, rasterPending);
          }
        }
        traceRecord('drag-raster-not-ready', {
          sourceZone: config.sourceZone,
          sourceType: config.sourceType || null,
        });
      }
    }

    // 0. Where the card is painted NOW — before any hover state is cleared. A
    //    drag that began on the inspection popup starts from the popup.
    const startPose = trackingPreviewPose || paintedPose(cardEl);
    playableLook = !!(cardEl.classList && cardEl.classList.contains('legal-play'));

    card = cardEl;
    activeConfig = config;
    data = {
      instanceId: config.instanceId,
      cardId: config.cardId,
      sourceZone: config.sourceZone,
      sourceType: config.sourceType,
      dropMode: config.dropMode === 'zone' ? 'zone' : 'target',
      element: cardEl,
      actionTraceId: actionTraceId
    };
    refusal = currentRefusal(config);

    traceRecord('drag-started', {
      pointer: { x: Math.round(curX), y: Math.round(curY) },
      sourceZone: data.sourceZone,
      sourceType: data.sourceType || null,
      dropMode: data.dropMode,
      refusal: refusal ? refusal.reason : null,
    });

    // 1. The drag owns the gesture now: dismiss any pending or latched touch
    //    preview and clear the shared popup, otherwise the
    //    max-z inspection popup obscures the battlefield for the whole drag.
    var acceptedPointerId = trackingPointerId;
    if (TouchPreview &&
        typeof TouchPreview.cancelActivePress === 'function') {
      TouchPreview.cancelActivePress({
        pointerId: acceptedPointerId,
        quiet: true,
      });
    }
    settleTouchPreviewHandoff(acceptedPointerId, true);
    if (Cards &&
        typeof Cards.clearCardPreview === 'function') {
      Cards.clearCardPreview();
    }
    if (CardHover &&
        typeof CardHover.clearActiveHandHover === 'function') {
      CardHover.clearActiveHandHover();
    }

    // 2. Drag classes. `card-dragging` suspends every hover engine; the mode
    //    class tells the stylesheet whether the table is a set of targets or a
    //    zone, so a summon never lights up an enemy.
    document.body.classList.add('card-dragging');
    document.body.classList.add(data.dropMode === 'zone' ? 'card-dragging--zone' : 'card-dragging--target');
    cardEl.classList.add('dragging');
    cardEl.style.visibility = 'hidden';
    /* A hand held still for a waiting draw (cards.js holdHandForDeal)
       answers the player the moment a card is lifted from it: the rest of the
       fan never sits on a paused glide under a drag, and the lifted card
       flies home to a seat that is not waiting to move. */
    if (data.sourceType === 'hand' && typeof Cards?.releaseHandForPlayer === 'function') {
      try {
        Cards.releaseHandForPlayer();
      } catch (_) { /* presentation only */ }
    }

    // 3. Hide hover overlay
    var overlay = document.getElementById('hover-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }

    // 4. Clear ALL hover / tilt / zoom state on every card (including the
    //    dragged card). Board cards use CSS zoom for hover enlargement; hand
    //    cards an inline hover transform. The fan's own rotation is a
    //    stylesheet value and is left alone: the hand keeps its shape while a
    //    card is carried away from it.
    document.querySelectorAll('.card').forEach(function (c) {
      if (c.classList.contains('card-drag-clone')) return;
      c.style.transition = 'none';
      c.classList.remove('hover-active', 'card-tilt-active', 'safari-hand-zoom-hover');
      c.style.removeProperty('--card-hover-transform');
      c.style.removeProperty('--card-tilt-rotate-x');
      c.style.removeProperty('--card-tilt-rotate-y');
      c.style.removeProperty('--card-tilt-opacity');
      c.style.transform = '';
      c.style.transformOrigin = '';
      c.style.zoom = '';
      c.style.margin = '';
      if (c._hoverTimer) {
        clearTimeout(c._hoverTimer);
        c._hoverTimer = undefined;
      }
    });

    // 5. Force layout reflow so rect reflects un-transformed, un-zoomed size
    cardEl.offsetHeight; // eslint-disable-line no-unused-expressions

    // 6. Capture origRect (un-hovered, un-tilted, un-zoomed), and the seat the
    //    card goes home to (the fan does not move while it is carried).
    origRect = cardEl.getBoundingClientRect();
    seatPose = config.sourceType === 'hand' || typeof config.homeContains === 'function'
      ? restingPoseOf(cardEl)
      : null;

    // 7. Set GameState
    if (typeof GameState !== 'undefined' && GameState.set) {
      GameState.set('draggedCard', data.instanceId);
      GameState.set('dragSourceZone', data.sourceZone);
      GameState.set('dragSourceType', data.sourceType || null);
    }

    // 8. Affordances. A targeted drag lights its candidate targets; a zone drag
    //    lights where the card will go; a refused drag lights nothing, because
    //    nothing it could reach would take it.
    if (!refusal) {
      if (data.dropMode === 'zone') markAvailableZones();
      else HighlightUtil.addPotentialTargetHighlights(data.instanceId);
    }

    // 9. Refresh portrait targets. Spell audio waits for the authoritative
    // spellCastStarted event; lifting a physical card is not acceptance.
    if (Indicators &&
        typeof Indicators.setupPortraitDropTargets === 'function') {
      try {
        Indicators.setupPortraitDropTargets();
      } catch (e) {
        console.warn('Could not refresh portrait hitboxes:', e);
      }
    }

    // 10. Create clone, start rAF loop
    dragging = true;
    settling = null;
    prevX = curX;
    prevY = curY;
    vx = 0;
    vy = 0;
    tiltX = 0;
    tiltY = 0;
    motionReducedForDrag = reducedMotion();
    lifting = !motionReducedForDrag;
    lastFrameAt = null;
    speedX = 0;
    lean = 0;
    leanVelocity = 0;
    overHome = false;
    leftHome = false;
    carryOffset = null;
    rising = false;
    magnetVel = null;
    nearCache = null;
    dragPointerType = trackingPointerType;
    liftStartedAt = null;
    liftElapsed = 0;
    armedAt = null;
    armedKey = null;
    stillSince = null;
    createClone(lifting ? startPose : null);
    liftFrom = Object.assign({}, pose);
    // The pickup swings the card toward where the pointer is heading.
    // The pickup's swing lands once the card has come down to its carried
    // size (a swing of the enlarged card would sweep half the screen).
    liftKickPending = lifting;
    syncRefusalChip();
    if (rasterPending) {
      const liftedClone = clone;
      rasterPending.then(function (ready) {
        if (!ready || clone !== liftedClone || !liftedClone.isConnected) return;
        try { Cards?.copyCanvasPaints?.(cardEl, liftedClone); } catch (_) { /* the copy keeps what it showed */ }
      });
    }
    rafId = requestAnimationFrame(update);
    return true;
  }

  /**
   * The card this drag carries was re-rendered (a snapshot rebuilt the hand or
   * the board). The node is not the identity — the instance is. Carry on with
   * the live node, hidden like the one it replaced, rather than cancelling a
   * gesture the player is in the middle of.
   */
  function adoptLiveSource(live) {
    card = live;
    if (data) data.element = live;
    live.classList.add('dragging');
    live.style.visibility = 'hidden';
  }

  // --- rAF loop ---
  function smoothing(dt, tau) {
    return 1 - Math.exp(-Math.max(0, dt) / tau);
  }

  /* A spring advanced in short fixed steps, whatever the frame length. Stepped
     once per frame, its first move grows with the SQUARE of the frame time: a
     single 32ms frame threw a card being pulled into its seat 90px, three
     times as far as the spring itself goes in that time (film, 2026-09-25). */
  const SPRING_STEP_S = 1 / 240;

  function stepSpring(state, target, stiffness, damping, seconds) {
    let left = Math.max(0, seconds);
    while (left > 1e-6) {
      const h = Math.min(SPRING_STEP_S, left);
      state.v += (stiffness * (target - state.x) - damping * state.v) * h;
      state.x += state.v * h;
      left -= h;
    }
    return state;
  }

  function update(frameTime) {
    if (!dragging) return;
    const now = Number.isFinite(frameTime) ? frameTime : nowMs();
    const dt = lastFrameAt === null ? 16.7 : clamp(now - lastFrameAt, 0, 64);
    lastFrameAt = now;

    // The source was replaced by a re-render: follow the instance, not the node.
    if (card && !card.isConnected) {
      const live = resolveLiveTrackedCard(card);
      if (!live) {
        cancel();
        return;
      }
      adoptLiveSource(live);
    }

    // 1. Velocity smoothing
    const speedScale = uiScale();
    if (stillSince === null || curX !== prevX || curY !== prevY) stillSince = now;
    const frameSpeedX = dt > 0 ? ((curX - prevX) * 1000) / dt : 0;
    speedX = lerp(speedX, frameSpeedX, 0.35);
    vx = lerp(vx, curX - prevX, VELOCITY_LERP);
    vy = lerp(vy, curY - prevY, VELOCITY_LERP);
    prevX = curX;
    prevY = curY;

    // 2. Tilt from velocity. Pointer speed is in screen pixels; per full-HD
    //    pixel, a drag across the same share of a bigger screen leans alike.
    var targetTiltX = clamp((-vy / speedScale) * TILT_FACTOR, -MAX_TILT, MAX_TILT);
    var targetTiltY = clamp((vx / speedScale) * TILT_FACTOR, -MAX_TILT, MAX_TILT);
    tiltX = lerp(tiltX, targetTiltX, TILT_LERP);
    tiltY = lerp(tiltY, targetTiltY, TILT_LERP);

    // 3. What the card can do right now (the turn may have changed under it),
    //    then where it would go.
    const nextRefusal = currentRefusal(activeConfig);
    if ((nextRefusal && nextRefusal.message) !== (refusal && refusal.message)
        || !!nextRefusal !== !!refusal) {
      refusal = nextRefusal;
      syncRefusalChip();
      markAvailableZones();
    }
    updateHover(hitTest(curX, curY));

    // Guard: if hovered target was detached from the DOM (renderBoard wipe),
    // clear it so the clone follows the cursor instead of snapping to (0,0).
    if (hovered && hovered.kind === 'element' && !hovered.el.isConnected) {
      updateHover(null);
    }

    // BACK over its own hand the card is home: letting go there puts it back.
    // Only back: at the pickup the pointer is still over the hand, and a card
    // shrunk to its resting size there reads as dropping back into the fan.
    let home = false;
    if (!hovered && activeConfig && typeof activeConfig.homeContains === 'function') {
      try { home = activeConfig.homeContains({ x: curX, y: curY }) === true; } catch (_) { home = false; }
    }
    if (!home) leftHome = true;
    home = home && leftHome;
    if (home !== overHome) {
      overHome = home;
      clone.classList.toggle('card-drag-clone--home', overHome);
    }
    updateNearness();

    // 4. Positioning
    const target = targetPose();
    const magnetised = target.magnet;

    // The lean: a spring toward a target set by sideways speed. A card drawn
    // to a slot or struck onto a target rights itself.
    const still = motionReducedForDrag;
    const leanTarget = still || magnetised
      ? 0
      : clamp((speedX / speedScale) * LEAN_PER_PX_S, -LEAN_MAX, LEAN_MAX);
    const dts = Math.min(dt, 32) / 1000;
    const leanState = { x: lean, v: leanVelocity };
    stepSpring(leanState, leanTarget, LEAN_STIFFNESS, LEAN_DAMPING, dts);
    leanVelocity = leanState.v;
    lean = clamp(leanState.x, -LEAN_MAX * 1.3, LEAN_MAX * 1.3);
    if (still) {
      lean = 0;
      leanVelocity = 0;
    }

    const scaleK = smoothing(dt, SCALE_TAU_MS);
    let settlingOffset = false;
    if (lifting) {
      if (liftStartedAt === null) liftStartedAt = now;
      else liftElapsed += Math.min(dt, LIFT_FRAME_MAX_MS);
      const t = clamp(liftElapsed / LIFT_MS, 0, 1);
      /* A quadratic ease, not a cubic: the cubic had most of a shrink from
         the enlarged card done inside one frame of film, and the lift read
         as a cut. */
      const move = easeOutQuad(t);
      /* Shrinking from an enlargement travels WITH the move: one motion toward
         the pointer. Held big while it slid (an ease-in-out shrink), the
         enlarged card still stood where it was on the first frame of film
         after the press, and was simply small on the next — "the preview
         pops to a small card" (blind critic, 2026-09-25). */
      const grow = target.scale > liftFrom.scale ? easeOutBack(t) : move;
      /* Off an enlargement that stands above the carry, it never sinks toward
         the hand on the way: "the grab reads as the card falling back down"
         (blind critic, 2026-09-25). It shrinks at its height and meets the
         pointer as the pointer comes up the table. */
      // (A card drawn to a target or a slot goes there: the rule is the carry's.)
      const liftY = !target.magnet && liftFrom.scale > target.scale ? Math.min(target.y, liftFrom.y) : target.y;
      pose.x = lerp(liftFrom.x, target.x, move);
      pose.y = lerp(liftFrom.y, liftY, move);
      pose.scale = lerp(liftFrom.scale, target.scale, grow);
      // The fan's angle eases away; the swing of the pickup is felt at once.
      pose.rotZ = lerp(liftFrom.rotZ || 0, 0, move) + lean;
      pose.lift = lerp(liftFrom.lift || 0, target.lift, move);
      if (liftKickPending && t >= LIFT_KICK_AT) {
        liftKickPending = false;
        const heading = Math.abs(speedX) > 30 ? Math.sign(speedX) : (curX < startX ? -1 : 1);
        leanVelocity += heading * LIFT_KICK_DEG_S;
      }
      if (t >= 1) {
        lifting = false;
        /* Lifted off an enlargement larger than it is ever carried, it was laid
           out at that size for the lift; now it is laid out at its carried
           size, once. The painted box is unchanged (the transform takes the
           difference), and the layout change is what makes every engine paint
           it afresh at the size it is now shown. */
        if (clone && clone._settledLayoutScale && Math.abs(clone._layoutScale - clone._settledLayoutScale) > 0.001) {
          layoutClone(clone, clone._settledLayoutScale);
        }
        carryOffset = { x: pose.x - curX, y: pose.y - curY };
        // Still above its carry (it did not sink on the way): it settles to it.
        settlingOffset = Math.hypot(pose.x - target.x, pose.y - target.y) > 1;
      }
    } else if (magnetised) {
      const k = hovered && hovered.kind === 'element' ? SNAP_LERP : smoothing(dt, MAGNET_TAU_MS);
      if (hovered && hovered.kind === 'element') {
        pose.x = lerp(pose.x, target.x, k);
        pose.y = lerp(pose.y, target.y, k);
        magnetVel = null;
      } else {
        // Picked up at the speed the card was already moving.
        if (!magnetVel) {
          const perSecond = 1000 / Math.max(8, dt);
          const cap = 1200;
          magnetVel = { x: clamp(vx * perSecond, -cap, cap), y: clamp(vy * perSecond, -cap, cap) };
        }
        const springX = { x: pose.x, v: magnetVel.x };
        const springY = { x: pose.y, v: magnetVel.y };
        stepSpring(springX, target.x, MAGNET_STIFFNESS, MAGNET_DAMPING, dts);
        stepSpring(springY, target.y, MAGNET_STIFFNESS, MAGNET_DAMPING, dts);
        pose.x = springX.x;
        pose.y = springY.x;
        magnetVel.x = springX.v;
        magnetVel.y = springY.v;
      }
      pose.scale = lerp(pose.scale, target.scale, k);
      pose.rotZ = lerp(pose.rotZ, (target.rotZ || 0) + lean, k);
      pose.lift = lerp(pose.lift, target.lift, k);
      restAtMagnetTarget(target);
      carryOffset = { x: pose.x - curX, y: pose.y - curY };
    } else {
      /* The card rides the pointer: its offset from the pointer is followed
         exactly, so the card never lags a finger. Only a CHANGE of offset —
         rising clear of the hand, settling back into it — is eased, together
         with size and height. */
      magnetVel = null;
      const want = { x: target.x - curX, y: target.y - curY };
      if (!carryOffset) carryOffset = { x: pose.x - curX, y: pose.y - curY };
      carryOffset.x = lerp(carryOffset.x, want.x, scaleK);
      carryOffset.y = lerp(carryOffset.y, want.y, scaleK);
      settlingOffset = Math.hypot(carryOffset.x - want.x, carryOffset.y - want.y) > 1;
      pose.x = curX + carryOffset.x;
      pose.y = curY + carryOffset.y;
      pose.scale = lerp(pose.scale, target.scale, scaleK);
      pose.rotZ = lean;
      pose.lift = lerp(pose.lift, target.lift, scaleK);
      if (!settlingOffset && !rising && !motionReducedForDrag) {
        const still = now - stillSince - IDLE_AFTER_MS;
        if (still > 0) {
          const amount = clamp(still / IDLE_FADE_MS, 0, 1);
          const phase = ((now - stillSince) / 1000) * IDLE_HZ * Math.PI * 2;
          pose.y += Math.sin(phase) * uiPx(IDLE_BOB_PX) * amount;
          pose.rotZ += Math.sin(phase * 0.8 + 1) * IDLE_SWAY_DEG * amount;
        }
      }
    }
    // 'free' is reserved for a card riding the pointer 1:1 (the lag oracle reads it).
    let phase = 'free';
    if (lifting) phase = 'lift';
    else if (overHome && magnetised) phase = 'home';
    else if (magnetised) phase = 'magnet';
    else if (rising) phase = 'rise';
    else if (settlingOffset) phase = 'settle';
    setPhase(clone, phase);
    syncGhost();
    syncCastRing();

    // 5. Position via left/top; transform only for tilt/scale decoration
    applyPose(clone, pose);
    levelRefusalChip();
    syncTether(magnetised);

    // 6. Drive glare via CSS vars (base 0.15 so glare is always subtly visible)
    var normX = clamp(0.5 + tiltY / (MAX_TILT * 2), 0, 1);
    var normY = clamp(0.5 - tiltX / (MAX_TILT * 2), 0, 1);
    var tiltMag = Math.sqrt(tiltX * tiltX + tiltY * tiltY) / MAX_TILT;
    clone.style.setProperty('--card-tilt-x', normX.toFixed(3));
    clone.style.setProperty('--card-tilt-y', normY.toFixed(3));
    // Capped: at full strength the glare washed the carried card out mid-lift
    // ("pale, semi-transparent, like two layers crossfading", blind critic).
    clone.style.setProperty('--card-tilt-opacity', clamp(0.12 + tiltMag * 0.33, 0, 0.45).toFixed(3));

    // 7. Rim light: red over a target it would strike, gold over the slot a
    //    summon would land in, violet once a release would cast a spell.
    let tone = restingTone();
    if (hovered && hovered.kind === 'element') tone = 'strike';
    else if (hovered && hovered.kind === 'zone') tone = hovered.cfg.release === 'consume' ? 'cast' : 'summon';
    if (clone._tone !== tone) {
      clone._tone = tone;
      clone.style.boxShadow = cloneGlow(tone, clone._layoutScale);
      clone.style.filter = tone === 'cast'
        ? 'brightness(1.16) saturate(1.1)'
        : (tone && tone !== 'refused' ? 'brightness(1.08)' : '');
    }

    rafId = requestAnimationFrame(update);
  }

  /* AT REST, NOT CREEPING. The pull into a slot or seat never quite arrives
     (a damped spring, an exponential ease): its last half pixel took another
     ~150 ms, and each time its edge crossed a pixel boundary the card stepped
     a whole pixel. Round 14 (26 September 2026, Chrome desktop, the meter's
     summon pickup): the card's last move came 470 ms after the pointer
     reached the slot, twice after it was under half a pixel from its pose.
     Under half a pixel and all but still, it is where it is going. This
     rests its place, size and height only: the lean it carries from the
     pointer's speed (the tilt in step 2) still eases out on its own, so the
     settle a player sees is about as long as before; what ends sooner is
     the box creeping and the ~450 ms of position writes. */
  const MAGNET_REST_PX = 0.5;
  const MAGNET_REST_PX_S = 30;
  const MAGNET_REST_SCALE = 0.002;
  const MAGNET_REST_DEG = 0.05;
  const MAGNET_REST_LIFT = 0.005;

  function restAtMagnetTarget(target) {
    if (Math.abs(target.x - pose.x) >= MAGNET_REST_PX || Math.abs(target.y - pose.y) >= MAGNET_REST_PX) return;
    if (magnetVel && Math.hypot(magnetVel.x, magnetVel.y) >= MAGNET_REST_PX_S) return;
    pose.x = target.x;
    pose.y = target.y;
    if (magnetVel) {
      magnetVel.x = 0;
      magnetVel.y = 0;
    }
    if (Math.abs(target.scale - pose.scale) < MAGNET_REST_SCALE) pose.scale = target.scale;
    const rotZ = (target.rotZ || 0) + lean;
    if (Math.abs(rotZ - pose.rotZ) < MAGNET_REST_DEG) pose.rotZ = rotZ;
    if (Math.abs(target.lift - pose.lift) < MAGNET_REST_LIFT) pose.lift = target.lift;
  }

  /** The pose the clone is heading for this frame. */
  function targetPose() {
    if (hovered && hovered.kind === 'element') {
      rising = false;
      var tRect = hovered.el.getBoundingClientRect();
      return {
        x: tRect.left + tRect.width / 2,
        y: tRect.top + tRect.height / 2,
        scale: SNAP_SCALE,
        lift: LIFT_HELD,
        magnet: true,
      };
    }
    const carried = carryPose();
    if (hovered && hovered.kind === 'zone') {
      let anchor = null;
      let magnet = false;
      try {
        magnet = typeof hovered.cfg.magnet === 'function'
          && hovered.cfg.magnet({ x: curX, y: curY }, data) === true;
        anchor = magnet && typeof hovered.cfg.anchor === 'function' ? hovered.cfg.anchor(data) : null;
      } catch (_) { anchor = null; }
      const anchorPose = anchor ? holdPoseFor(anchor) : null;
      if (anchorPose) {
        rising = false;
        return Object.assign(anchorPose, { magnet: true });
      }
      return Object.assign(clearOfHand(carried), { scale: carried.scale * ARMED_BOOST * armPulse(), magnet: false });
    }
    if (overHome) {
      rising = false;
      /* It previews going home: drawn into its own seat, at the seat's size
         and fan angle, so letting go there is a settle — not a flight that
         parks above the hand and then corrects into it. */
      if (seatPose) return Object.assign({}, seatPose, { lift: LIFT_HOME * 0.35, magnet: true });
      return Object.assign(carried, { scale: HOME_SCALE, lift: LIFT_HOME, magnet: false });
    }
    return Object.assign(clearOfHand(carried), { magnet: false });
  }

  /* A card lifted out of the hand rises CLEAR of it at once, though the
     pointer that grabbed it is still inside the fan; a carry pinned to the
     pointer sat on the hand line until the pointer left it, and the grab read
     as a drift (blind critic, 2026-09-25). Continuous in the pointer: the card
     rides it again as soon as riding it keeps the card above the hand. */
  function clearOfHand(carried) {
    rising = false;
    if (!activeConfig || typeof activeConfig.homeTop !== 'function') return carried;
    let top = NaN;
    try { top = Number(activeConfig.homeTop()); } catch (_) { top = NaN; }
    if (!Number.isFinite(top)) return carried;
    const ceiling = top - (cardSize().height * carryScale()) / 2 - HAND_CLEARANCE_PX;
    if (carried.y > ceiling) {
      carried.y = ceiling;
      rising = true;
    }
    return carried;
  }

  // --- What a release would do, drawn on the table ---
  /* A picture of the card waits in the slot a summon would land in while a
     release would summon — the one thing a lit outline never said: THIS card
     goes THERE. Built once per drag from the card itself, never a second copy
     of it: no id, no instance id, hidden from assistive technology. */
  function ensureGhost() {
    if (ghost || !card) return ghost;
    /** @type {any} */
    const el = card.cloneNode(true);
    try { Cards?.copyCanvasPaints?.(card, el); } catch (_) { /* the frame alone still says where */ }
    el.id = '';
    delete el.dataset.instanceId;
    delete el.dataset.dragPhase;
    delete el.dataset.dragHeldArrival;
    el.classList.remove(
      'hover-active', 'dragging', 'potential-target', 'valid-target', 'legal-play', 'card-tilt-active',
      'safari-hand-zoom-hover', 'draggable', 'interactive-card', 'tutorial-allowed', 'card-draw-in',
      'fx-draw-hold', 'card-cohort-hold', 'invalid-shake'
    );
    el.classList.add('card-drag-ghost', 'card-tilt-disabled');
    el.removeAttribute('aria-describedby');
    el.setAttribute('aria-hidden', 'true');
    // Keep the face's own inline values; drop where and how the seat drew it.
    [
      'visibility', 'opacity', 'transform', 'transform-origin', 'transition', 'zoom', 'margin',
      'position', 'left', 'top', 'right', 'bottom', 'z-index', 'display', 'filter', 'box-shadow',
      '--card-base-transform', '--card-hover-transform', '--card-tilt-rotate-x', '--card-tilt-rotate-y',
      '--card-tilt-opacity',
    ].forEach(function (name) { el.style.removeProperty(name); });
    const size = cardSize();
    el.style.width = size.width + 'px';
    el.style.height = size.height + 'px';
    // Inline, like the drag clone: the battle's own `.card` rules would
    // otherwise seat it in the body's flow, off the table.
    el.style.position = 'fixed';
    el.style.zIndex = '14990';
    el.style.margin = '0';
    el.style.pointerEvents = 'none';
    el.style.transformOrigin = 'center center';
    el._pw = size.width;
    el._ph = size.height;
    document.body.appendChild(el);
    ghost = el;
    return ghost;
  }

  function syncGhost() {
    let anchor = null;
    if (hovered && hovered.kind === 'zone' && hovered.cfg.release === 'hold' && !refusal) {
      try { anchor = typeof hovered.cfg.anchor === 'function' ? hovered.cfg.anchor(data) : null; } catch (_) { anchor = null; }
    }
    const rect = anchor && typeof anchor.getBoundingClientRect === 'function' ? anchor.getBoundingClientRect() : null;
    if (!rect || !(rect.width > 0)) {
      if (ghost) ghost.classList.remove('card-drag-ghost--shown');
      return;
    }
    const el = ensureGhost();
    if (!el) return;
    const left = rect.left + rect.width / 2 - el._pw / 2;
    const top = rect.top + rect.height / 2 - el._ph / 2;
    if (el._at !== `${left}|${top}`) {
      el._at = `${left}|${top}`;
      el.style.left = left + 'px';
      el.style.top = top + 'px';
      el.style.transform = `scale(${(rect.width / el._pw).toFixed(3)})`;
    }
    el.classList.add('card-drag-ghost--shown');
  }

  function removeGhost({ fade = false } = {}) {
    const el = ghost;
    ghost = null;
    if (!el) return;
    if (!fade) {
      el.remove();
      return;
    }
    el.classList.remove('card-drag-ghost--shown');
    scheduleDelayed(function () { el.remove(); }, 200);
  }

  /* An untargeted Spell over the battlefield: a mark on the table under the
     pointer, where letting go would cast it. */
  function syncCastRing() {
    const show = !!(hovered && hovered.kind === 'zone' && hovered.cfg.release === 'consume' && !refusal);
    if (!show && !castRing) return;
    if (!castRing) {
      castRing = document.createElement('div');
      castRing.className = 'card-drag-cast-ring';
      castRing.setAttribute('aria-hidden', 'true');
      document.body.appendChild(castRing);
    }
    castRing.classList.toggle('card-drag-cast-ring--shown', show);
    if (show) castRing.style.transform = `translate(${curX.toFixed(1)}px, ${curY.toFixed(1)}px) translate(-50%, -50%)`;
  }

  function removeCastRing() {
    if (castRing) castRing.remove();
    castRing = null;
  }

  /** The burst of light a Spell is spent in, where it was let go. */
  function spawnCastFlash(p, { delayMs = 0 } = {}) {
    const flash = document.createElement('div');
    flash.className = 'card-drag-cast-flash';
    flash.setAttribute('aria-hidden', 'true');
    flash.style.setProperty('--cast-flash-delay', `${Math.round(delayMs)}ms`);
    const size = cardSize();
    const diameter = Math.max(size.width, size.height) * p.scale * 1.3;
    flash.style.width = diameter + 'px';
    flash.style.height = diameter + 'px';
    flash.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) translate(-50%, -50%)`;
    /* The card SHATTERS into sparks: they start all over its face (not from
       one point) and fly outward and up, big enough to read at a glance, and
       outlive the flash — the card becomes the spell. */
    if (!motionReducedForDrag) {
      const motes = 48;
      const halfW = (size.width * p.scale) / 2;
      const halfH = (size.height * p.scale) / 2;
      for (let i = 0; i < motes; i += 1) {
        const mote = document.createElement('span');
        mote.className = 'card-drag-cast-mote';
        const sx = (Math.random() * 2 - 1) * halfW * 0.85;
        const sy = (Math.random() * 2 - 1) * halfH * 0.85;
        const out = Math.atan2(sy, sx) + (Math.random() - 0.5) * 0.9;
        const reach = diameter * (0.35 + Math.random() * 0.55);
        mote.style.setProperty('--sx', `${sx.toFixed(1)}px`);
        mote.style.setProperty('--sy', `${sy.toFixed(1)}px`);
        mote.style.setProperty('--dx', `${(Math.cos(out) * reach).toFixed(1)}px`);
        mote.style.setProperty('--dy', `${(Math.sin(out) * reach * 0.8 - diameter * 0.22).toFixed(1)}px`);
        mote.style.setProperty('--mote', (1.2 + Math.random() * 1.4).toFixed(2));
        mote.style.animationDelay = `${Math.round(delayMs + Math.random() * 120)}ms`;
        flash.appendChild(mote);
      }
    }
    document.body.appendChild(flash);
    // Not tied to this interaction: the light outlives the card it came from.
    setTimeout(function () { flash.remove(); }, delayMs + CAST_FLASH_MS + 260);
  }

  /* The slot a summon will land in answers the approaching card: its light
     rises as the pointer nears it (`--drop-near`, 0..1), so the player sees
     where the card is going long before the release. */
  function updateNearness() {
    if (!nearMarks.length) return;
    const now = nowMs();
    if (!nearCache || now - nearCache.at > 200) {
      nearCache = {
        at: now,
        marks: nearMarks.map(function (el) {
          const rect = typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
          return rect && rect.height > 0
            ? { el, x: rect.left + rect.width / 2, y: rect.top + rect.height / 2, reach: rect.height * NEAR_REACH_HEIGHTS }
            : { el, x: NaN, y: NaN, reach: 1 };
        }),
      };
    }
    nearCache.marks.forEach(function (mark) {
      const distance = Math.hypot(curX - mark.x, curY - mark.y);
      const near = overHome || !Number.isFinite(distance) ? 0 : clamp(1 - distance / mark.reach, 0, 1);
      const value = near.toFixed(2);
      if (mark.el.style.getPropertyValue('--drop-near') !== value) {
        mark.el.style.setProperty('--drop-near', value);
      }
    });
  }

  function clearAvailableMarks() {
    nearMarks.forEach(function (el) { el?.style?.removeProperty?.('--drop-near'); });
    nearMarks = [];
    nearCache = null;
    return clearMarks(availableMarks, 'drop-zone-available');
  }

  /** Hanging over `anchor`: centred on it, lifted off the table, a little large. */
  function holdPoseFor(anchor) {
    const rect = anchor && typeof anchor.getBoundingClientRect === 'function'
      ? anchor.getBoundingClientRect()
      : null;
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) return null;
    const size = cardSize();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2 - size.height * HOLD_RAISE,
      scale: HOLD_SCALE,
      rotZ: 0,
      opacity: 1,
      lift: LIFT_HELD,
    };
  }

  // --- Hit test ---
  function hitTest(x, y) {
    if (!clone || !data) return null;
    if (refusal) return null;

    if (data.dropMode === 'zone') {
      lastHitTestDebug = {
        pointer: { x: Math.round(x), y: Math.round(y) },
        candidates: [],
        resolved: null,
        targetZone: null,
      };
      for (const [key, cfg] of zones) {
        try {
          if (typeof cfg.contains === 'function' && !cfg.contains({ x, y }, data)) continue;
          if (typeof cfg.accepts === 'function' && !cfg.accepts(data)) continue;
        } catch (_) {
          continue;
        }
        lastHitTestDebug.resolved = { zone: key };
        lastHitTestDebug.targetZone = cfg.zone || key;
        return { kind: 'zone', key, cfg };
      }
      return null;
    }

    // Hide clone (visibility, not display, to avoid layout reflow)
    clone.style.visibility = 'hidden';
    var elements = document.elementsFromPoint(x, y);
    clone.style.visibility = '';

    lastHitTestDebug = {
      pointer: { x: Math.round(x), y: Math.round(y) },
      candidates: elements.slice(0, 6).map(describeElement).filter(Boolean),
      resolved: null,
      targetZone: null,
    };

    // Walk up DOM from each hit element to find a registered target
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      while (el && el !== document.body) {
        if (targets.has(el)) {
          // Skip self
          if (el === card) {
            break;
          }
          var cfg = targets.get(el);
          if (cfg.validate && !cfg.validate(data, el)) {
            break;
          }
          lastHitTestDebug.resolved = describeElement(el);
          lastHitTestDebug.targetZone = cfg.zone || null;
          return { kind: 'element', el, cfg };
        }
        el = el.parentElement;
      }
    }
    return null;
  }

  /** Would a release here have been an attempt to PLAY (ignoring legality)? */
  function releaseWasAnAttempt(x, y) {
    if (!data) return false;
    if (data.dropMode === 'zone') {
      for (const cfg of zones.values()) {
        try {
          if (typeof cfg.contains === 'function' && cfg.contains({ x, y }, data)) return true;
        } catch (_) { /* keep looking */ }
      }
      return false;
    }
    if (!clone) return false;
    clone.style.visibility = 'hidden';
    const elements = document.elementsFromPoint(x, y);
    clone.style.visibility = '';
    return elements.some(function (el) {
      for (let node = el; node && node !== document.body; node = node.parentElement) {
        if (node !== card && targets.has(node)) return true;
      }
      return false;
    });
  }

  // --- Update hover ---
  function sameHover(a, b) {
    if (a === b) return true;
    if (!a || !b || a.kind !== b.kind) return false;
    return a.kind === 'element' ? a.el === b.el : a.key === b.key;
  }

  function updateHover(hit) {
    if (sameHover(hit, hovered)) return;
    if (hovered && hovered.kind === 'element') {
      hovered.el.classList.remove('valid-target');
    }
    armedMarks = clearMarks(armedMarks, 'drop-zone-armed');
    hovered = hit || null;
    hoveredTarget = hovered && hovered.kind === 'element' ? hovered.el : null;
    if (hovered && hovered.kind === 'element') {
      hovered.el.classList.add('valid-target');
    } else if (hovered && hovered.kind === 'zone') {
      try {
        const armed = typeof hovered.cfg.armed === 'function' ? hovered.cfg.armed(data) : [];
        armedMarks = markAll(armed, 'drop-zone-armed');
      } catch (_) { armedMarks = []; }
    }
    const nextArmedKey = hovered && hovered.kind === 'zone' ? hovered.key : null;
    if (nextArmedKey !== armedKey) {
      armedKey = nextArmedKey;
      armedAt = nextArmedKey !== null ? (lastFrameAt === null ? nowMs() : lastFrameAt) : null;
    }
  }

  // --- Tweens (a released clone) ---
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function easeOutQuad(t) { return 1 - (1 - t) * (1 - t); }
  function easeInOutQuad(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
  function easeOutBack(t) {
    const c1 = 1.2;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }
  // Each ease in both forms: CSS for the compositor, a function for the fallback.
  const EASE_OUT = { fn: easeOutCubic, css: 'cubic-bezier(0.33, 1, 0.68, 1)' };
  const EASE_BACK = { fn: easeOutBack, css: 'cubic-bezier(0.34, 1.32, 0.64, 1)' };
  // The glide to a landing slot: away from the release point, across, and
  // into the hang, with its fastest travel in the middle where the eye follows it.
  function easeGlide(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  const EASE_GLIDE = { fn: easeGlide, css: 'cubic-bezier(0.65, 0, 0.35, 1)' };
  // Accelerating into a point: a collapse, not a fade.
  function easeInCubic(t) { return t * t * t; }
  const EASE_IN = { fn: easeInCubic, css: 'cubic-bezier(0.32, 0, 0.67, 0)' };
  const EASE_LINEAR = { fn: function (t) { return t; }, css: 'linear' };

  /** The painted pose of a released clone, read from the page (a running animation included). */
  function livePose(el, fallback) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return fallback;
    const rect = el.getBoundingClientRect();
    const width = el._pw ? el._pw / (el._layoutScale || 1) : rect.width;
    if (!(rect.width > 0) || !(width > 0)) return fallback;
    return Object.assign({}, fallback, {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      scale: rect.width / width,
    });
  }

  /**
   * Animate a released clone from `from` to `to`. Returns a controller; `done`
   * runs once, on completion or on the safety timer (a hidden tab has no
   * frames), never after `cancel`.
   *
   * ON THE COMPOSITOR, not the frame clock. The moment a card is let go is the
   * moment the page is busiest — the server's snapshot rebuilds the board and
   * the hand — and a frame-driven tween freezes for that stall and then jumps
   * to its end: measured 200ms of no frames on a desktop summon, during which
   * the card "teleported" from where it was released to its slot. A transform
   * animation keeps running while the main thread is blocked. The element is
   * seated at its END pose and the animation carries it there from `from`.
   */
  function tweenClone(el, from, to, { duration, easing = EASE_OUT, done, arc = 0 }) {
    if (el && typeof el.animate === 'function') {
      return animateClone(el, from, to, { duration, easing, done, arc });
    }
    return frameTweenClone(el, from, to, { duration, easing, done, arc });
  }

  /**
   * The pose at progress `k` from `start` to `end`. With an `arc`, the centre
   * travels a quadratic curve that bows UP by that many pixels at its middle —
   * a card thrown back to the hand arcs over the table instead of sliding.
   */
  function poseAt(start, end, k, arc) {
    const p = {};
    Object.keys(end).forEach(function (key) {
      const a = start[key] === undefined ? end[key] : start[key];
      p[key] = typeof end[key] === 'number' && typeof a === 'number' ? lerp(a, end[key], k) : end[key];
    });
    if (arc) p.y -= 4 * arc * k * (1 - k);
    return p;
  }

  function arcFor(from, to) {
    const size = cardSize();
    const distance = Math.hypot((to.x || 0) - (from.x || 0), (to.y || 0) - (from.y || 0));
    return Math.min(distance * RETURN_ARC, size.height * 0.5);
  }

  // Native effects can finish painting before an engine delivers their
  // completion promise or timers. Observe the native clock on painted frames;
  // this never drives the motion or advances an effect that has not started.
  function followNativePlayback(el, animation, visit) {
    let stopped = false;
    let frame = null;
    const tick = function () {
      if (stopped || !el.isConnected || animation.playState === 'idle') {
        stopped = true;
        return;
      }
      const current = animation.currentTime;
      if (typeof current === 'number' && Number.isFinite(current) && animation.startTime !== null) visit(current);
      if (!stopped) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return function () {
      stopped = true;
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }

  function animateClone(el, from, to, { duration, easing, done, arc = 0 }) {
    const start = Object.assign({}, from);
    const end = Object.assign({}, start, to);
    const shadow = el._dragShadow || null;
    // Sampled along the curve; the easing runs over the whole flight.
    const steps = arc ? [0, 0.25, 0.5, 0.75, 1] : [0, 1];
    const poses = steps.map(function (k) { return poseAt(start, end, k, arc); });
    const frames = poses.map(function (p, i) {
      return {
        transform: `translate(${(p.x - end.x).toFixed(2)}px, ${(p.y - end.y).toFixed(2)}px) ${cloneTransform(p, el)}`,
        opacity: p.opacity === undefined ? 1 : p.opacity,
        offset: steps[i],
      };
    });
    let finished = false;
    let animation = null;
    let shadowAnimation = null;
    let safety = null;
    let stopPlayback = null;
    const finish = function () {
      if (finished) return;
      finished = true;
      stopPlayback?.();
      if (safety) {
        clearTimeout(safety);
        delayedTimers.delete(safety);
      }
      applyPose(el, end);
      if (typeof done === 'function') done();
    };
    applyPose(el, end);
    try {
      animation = el.animate(frames, { duration, easing: easing.css });
      if (shadow && typeof shadow.animate === 'function') {
        shadowAnimation = shadow.animate(poses.map(function (p, i) {
          return { transform: shadowTransform(shadow, p), opacity: shadowOpacity(p), offset: steps[i] };
        }), { duration, easing: easing.css });
      }
      animation.finished.then(finish, function () { /* cancelled: the caller took over */ });
      stopPlayback = followNativePlayback(el, animation, function (current) {
        if (current >= duration) finish();
      });
    } catch (_) {
      return frameTweenClone(el, start, to, { duration, easing, done, arc });
    }
    safety = scheduleDelayed(finish, duration + 250);
    return {
      cancel() {
        if (finished) return;
        finished = true;
        stopPlayback?.();
        if (safety) {
          clearTimeout(safety);
          delayedTimers.delete(safety);
        }
        try { animation?.cancel(); } catch (_) { /* already gone */ }
        try { shadowAnimation?.cancel(); } catch (_) { /* already gone */ }
      },
      finish,
    };
  }

  /** The frame-clock fallback, for an engine without Web Animations. */
  function frameTweenClone(el, from, to, { duration, easing, done, arc = 0 }) {
    const start = Object.assign({}, from);
    const end = Object.assign({}, start, to);
    let startedAt = null;
    let finished = false;
    let frame = null;
    const finish = function () {
      if (finished) return;
      finished = true;
      if (frame) cancelAnimationFrame(frame);
      applyPose(el, end);
      if (typeof done === 'function') done();
    };
    const step = function (frameTime) {
      if (finished) return;
      const now = Number.isFinite(frameTime) ? frameTime : nowMs();
      if (startedAt === null) startedAt = now;
      const t = clamp((now - startedAt) / duration, 0, 1);
      applyPose(el, poseAt(start, end, easing.fn(t), arc));
      if (t >= 1) {
        finish();
        return;
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    const safety = scheduleDelayed(finish, duration + 120);
    return {
      cancel() {
        finished = true;
        if (frame) cancelAnimationFrame(frame);
        clearTimeout(safety);
        delayedTimers.delete(safety);
      },
      finish,
    };
  }

  /**
   * A clone driven through several poses as ONE motion, scheduled at once:
   * `stops` are { pose, duration, easing, filter } in order. On an engine with
   * Web Animations it is a single animation (and a single one for its
   * shadow), so a busy page cannot stretch the beats apart; the frame-clock
   * fallback walks the same path. `onStop(i)` runs as stop i is reached —
   * on the frame clock only; a compositor path times its own effects.
   */
  function tweenClonePath(el, from, stops, { done, onStop = null }) {
    const start = Object.assign({}, from);
    const poses = [start];
    stops.forEach(function (stop) { poses.push(Object.assign({}, poses[poses.length - 1], stop.pose)); });
    const end = poses[poses.length - 1];
    const total = stops.reduce(function (sum, stop) { return sum + stop.duration; }, 0);
    const offsets = [0];
    stops.forEach(function (stop) { offsets.push(offsets[offsets.length - 1] + stop.duration / total); });
    const shadow = el._dragShadow || null;
    let finished = false;
    const finish = function () {
      if (finished) return;
      finished = true;
      applyPose(el, end);
      if (typeof done === 'function') done();
    };
    if (typeof el.animate === 'function') {
      try {
        applyPose(el, end);
        const frames = poses.map(function (p, i) {
          const frame = {
            transform: `translate(${(p.x - end.x).toFixed(2)}px, ${(p.y - end.y).toFixed(2)}px) ${cloneTransform(p, el)}`,
            opacity: p.opacity === undefined ? 1 : p.opacity,
            offset: Math.min(1, offsets[i]),
          };
          if (i < stops.length) frame.easing = stops[i].easing.css;
          const filter = i === 0 ? (el.style.filter || 'none') : stops[i - 1].filter;
          if (filter) frame.filter = filter;
          return frame;
        });
        const animation = el.animate(frames, { duration: total, fill: 'forwards' });
        if (shadow && typeof shadow.animate === 'function') {
          shadow.animate(poses.map(function (p, i) {
            const frame = { transform: shadowTransform(shadow, p), opacity: shadowOpacity(p), offset: Math.min(1, offsets[i]) };
            if (i < stops.length) frame.easing = stops[i].easing.css;
            return frame;
          }), { duration: total, fill: 'forwards' });
        }
        animation.finished.then(finish, function () { /* cancelled: the caller took over */ });
        scheduleDelayed(finish, total + 250);
        return { cancel() { finished = true; try { animation.cancel(); } catch (_) { /* gone */ } }, finish };
      } catch (_) { /* the frame clock walks it instead */ }
    }
    let startedAt = null;
    let reached = 0;
    const step = function (frameTime) {
      if (finished) return;
      const now = Number.isFinite(frameTime) ? frameTime : nowMs();
      if (startedAt === null) startedAt = now;
      const t = clamp((now - startedAt) / total, 0, 1);
      let i = 0;
      while (i < stops.length - 1 && t >= offsets[i + 1]) i += 1;
      while (reached < stops.length && t >= offsets[reached + 1] - 1e-9) {
        if (stops[reached].filter) el.style.filter = stops[reached].filter;
        if (typeof onStop === 'function') onStop(reached);
        reached += 1;
      }
      const span = offsets[i + 1] - offsets[i];
      const k = span > 0 ? clamp((t - offsets[i]) / span, 0, 1) : 1;
      applyPose(el, poseAt(poses[i], poses[i + 1], stops[i].easing.fn(k), 0));
      if (t >= 1) finish();
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    scheduleDelayed(finish, total + 120);
    return { cancel() { finished = true; }, finish };
  }

  /** The shadow alone, closing onto the table under a card that has left it. */
  function settleShadow(shadow, from, to, duration) {
    if (!shadow) return;
    const end = Object.assign({}, from, to);
    const remove = function () { shadow.remove(); };
    if (typeof shadow.animate === 'function') {
      applyShadow(shadow, end);
      try {
        const animation = shadow.animate([
          { transform: shadowTransform(shadow, from), opacity: shadowOpacity(from) },
          { transform: shadowTransform(shadow, end), opacity: shadowOpacity(end) },
        ], { duration, easing: 'cubic-bezier(0.55, 0, 0.85, 0.35)' });
        animation.finished.then(remove, remove);
      } catch (_) {
        remove();
        return;
      }
      scheduleDelayed(remove, duration + 250);
      return;
    }
    let startedAt = null;
    const step = function (frameTime) {
      if (!shadow.isConnected) return;
      const now = Number.isFinite(frameTime) ? frameTime : nowMs();
      if (startedAt === null) startedAt = now;
      const t = clamp((now - startedAt) / duration, 0, 1);
      applyShadow(shadow, poseAt(from, end, t * t, 0));
      if (t >= 1) remove();
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    scheduleDelayed(remove, duration + 120);
  }

  // --- Drop ---
  function drop() {
    if (!dragging) return;
    dragging = false;
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    // Whatever a release does, the marks of what it WOULD do are over — but
    // for the ghost of a summon, which waits for the card it stands for.
    if (castRing) castRing.classList.remove('card-drag-cast-ring--shown');
    removeTether();

    // The animation-frame loop may not have run since the last pointer event.
    // Resolve the release point synchronously so a fast first drag cannot use a
    // stale target (or no target) merely because pointerup beat the next frame.
    refusal = currentRefusal(activeConfig);
    updateHover(hitTest(curX, curY));

    // Re-validate the hovered element at drop time
    if (hovered && hovered.kind === 'element') {
      // Guard: target may have been detached by a mid-drag board re-render
      if (!hovered.el.isConnected) {
        updateHover(null);
      } else if (hovered.cfg && hovered.cfg.validate && !hovered.cfg.validate(data, hovered.el)) {
        updateHover(null);
      }
    }

    const holding = !!(hovered && hovered.kind === 'zone' && clone && hovered.cfg.release === 'hold');
    if (ghost && !holding) ghost.classList.remove('card-drag-ghost--shown');

    if (hovered && hovered.kind === 'element' && clone) {
      dropOnElement();
    } else if (hovered && hovered.kind === 'zone' && clone) {
      if (hovered.cfg.release === 'hold') holdOverAnchor();
      else consumeInZone();
    } else {
      validDrop = false;
      const attempted = !!refusal && releaseWasAnAttempt(curX, curY);
      traceFinish('drop-rejected', Object.assign({}, lastHitTestDebug || {}, {
        reason: !clone ? 'missing-drag-clone' : (refusal ? `refused-${refusal.reason}` : 'no-valid-target'),
      }));
      returnHome({ refused: attempted ? refusal : null });
    }
  }

  /** A targeted drop: slide onto the exact target, fade, dispatch. */
  function dropOnElement() {
    // Valid drop — finish the magnetic slide at the exact painted target,
    // then fade and dispatch. A fast finger can release before another rAF;
    // without this synchronous destination the card disappears a quarter of
    // the way toward an opponent-hand card and appears to choose the wrong one.
    // The source card stays hidden; the board re-render (triggered by the
    // game action) will create fresh DOM elements for all cards.
    validDrop = true;
    settling = 'slide';
    const target = hovered.el;
    var acceptedRect = target.getBoundingClientRect();
    pose = {
      x: acceptedRect.left + acceptedRect.width / 2,
      y: acceptedRect.top + acceptedRect.height / 2,
      scale: SNAP_SCALE,
      rotZ: 0,
      opacity: 0,
    };
    tiltX = 0;
    tiltY = 0;
    setPhase(clone, 'slide');
    clone.style.transition = 'left 120ms ease-out, top 120ms ease-out, transform 120ms ease-out, opacity 120ms ease';
    if (clone._dragShadow) clone._dragShadow.style.transition = 'transform 120ms ease-out, opacity 120ms ease';
    applyPose(clone, pose);
    clone.style.opacity = '0';

    var dropCfg = targets.get(target);
    var dropData = Object.assign({}, data);
    var dropGeneration = interactionGeneration;
    var dropTraceId = actionTraceId;
    traceRecord('drop-accepted', Object.assign({}, lastHitTestDebug || {}, {
      targetZone: dropCfg?.zone || null,
    }));

    scheduleDelayed(function () {
      var dropResult = null;
      var dispatched = false;
      // cleanup() MUST run even if the drop handler throws synchronously,
      // otherwise the faded-out clone is orphaned in the DOM forever.
      try {
        if (dropCfg && typeof dropCfg.onDrop === 'function') {
          traceRecordFor(dropTraceId, 'drop-dispatched', { targetZone: dropCfg.zone || null });
          dropResult = dropCfg.onDrop(dropData, target);
          dispatched = true;
          if (dropResult && typeof dropResult.then === 'function') {
            void Promise.resolve(dropResult).catch(function (err) {
              traceFinishFor(dropTraceId, 'drop-handler-rejected', {
                message: err && err.message,
              });
              console.warn('[CardDrag] async onDrop handler rejected:', err && err.message);
            });
          }
        }
      } catch (err) {
        traceFinishFor(dropTraceId, 'drop-handler-threw', { message: err && err.message });
        console.warn('[CardDrag] onDrop handler threw; cleaning up clone anyway:', err && err.message);
      } finally {
        // A different pointer interaction may have started during this
        // drop's fade. Never let an older timer tear down the newer drag.
        if (interactionGeneration === dropGeneration) cleanup();
        // cleanup() is what APPLIES the dim (or takes a hand card out of the
        // hand), so either can only be tied to this action afterwards.
        // Whatever is pending now is this drop's.
        bindPendingActionDim(pendingActionDim, dropResult, dispatched);
        bindPendingHandPlay(latestHandPlay, dropResult, dispatched);
        latestHandPlay = null;
      }
    }, 130);
  }

  function dispatchZoneDrop(cfg, dropData, traceId, zoneKey) {
    let result = null;
    let dispatched = false;
    try {
      if (typeof cfg.onDrop === 'function') {
        traceRecordFor(traceId, 'drop-dispatched', { targetZone: cfg.zone || zoneKey });
        result = cfg.onDrop(dropData);
        dispatched = true;
        if (result && typeof result.then === 'function') {
          void Promise.resolve(result).catch(function (err) {
            traceFinishFor(traceId, 'drop-handler-rejected', { message: err && err.message });
            console.warn('[CardDrag] async zone drop rejected:', err && err.message);
          });
        }
      }
    } catch (err) {
      traceFinishFor(traceId, 'drop-handler-threw', { message: err && err.message });
      console.warn('[CardDrag] zone drop handler threw:', err && err.message);
    }
    return { result, dispatched };
  }

  /**
   * An untargeted Spell: spent where it was released. It GATHERS — rises a
   * little, swells and brightens — and is then spent in light where it stands.
   * The cast itself is the server's to announce: its surge starts from the
   * point recorded here (castPointOf), because that is where the card was.
   */
  const castPoints = new Map();

  function castPointOf(instanceId) {
    const entry = instanceId === undefined || instanceId === null ? null : castPoints.get(String(instanceId));
    if (!entry) return null;
    if (nowMs() - entry.at > CAST_POINT_TTL_MS) {
      castPoints.delete(String(instanceId));
      return null;
    }
    return Object.assign({}, entry.rect);
  }

  function rememberCastPoint(instanceId, p) {
    if (instanceId === undefined || instanceId === null || !p) return;
    const size = cardSize();
    const width = size.width * p.scale;
    const height = size.height * p.scale;
    const left = p.x - width / 2;
    const top = p.y - height / 2;
    castPoints.set(String(instanceId), {
      at: nowMs(),
      rect: { left, top, width, height, right: left + width, bottom: top + height },
    });
  }

  function consumeInZone() {
    validDrop = true;
    settling = 'consume';
    const cfg = hovered.cfg;
    const zoneKey = hovered.key;
    const dropData = Object.assign({}, data);
    const generation = interactionGeneration;
    const traceId = actionTraceId;
    traceRecord('drop-accepted', Object.assign({}, lastHitTestDebug || {}, { targetZone: cfg.zone || zoneKey }));
    setPhase(clone, 'consume');
    const el = clone;
    const size = cardSize();
    // ONE cast per release: whichever of the dispatch beat and the end of the
    // dissolve comes first sends it, and the other finds it already sent.
    let outcome = { result: null, dispatched: false };
    scheduleDelayed(function () {
      if (interactionGeneration !== generation || outcome.dispatched) return;
      outcome = dispatchZoneDrop(cfg, dropData, traceId, zoneKey);
    }, CONSUME_DISPATCH_MS);
    tiltX = lerp(tiltX, 0, 0.5);
    tiltY = lerp(tiltY, 0, 0.5);
    const finish = function () {
      if (interactionGeneration !== generation) return;
      if (!outcome.dispatched) outcome = dispatchZoneDrop(cfg, dropData, traceId, zoneKey);
      cleanup();
      bindPendingActionDim(pendingActionDim, outcome.result, outcome.dispatched);
      bindPendingHandPlay(latestHandPlay, outcome.result, outcome.dispatched);
      latestHandPlay = null;
    };

    if (reducedMotion()) {
      // No travel: the card is spent where it stands, in a short fade.
      rememberCastPoint(dropData.instanceId, pose);
      tweenClone(el, pose, Object.assign({}, pose, { opacity: 0 }), {
        duration: CONSUME_MS, easing: EASE_OUT, done: finish,
      });
      return;
    }

    const carried = Object.assign({}, pose, { rotZ: 0 });
    const woundUp = Object.assign({}, carried, {
      y: carried.y - size.height * 0.12,
      scale: carried.scale * CONSUME_WINDUP_SCALE,
      lift: 1.45,
      opacity: 1,
    });
    const struck = Object.assign({}, carried, {
      y: carried.y + size.height * 0.04,
      scale: carried.scale * 0.9,
      squash: 1,
      lift: 0.3,
      opacity: 1,
    });
    /* Turned to light IN PLACE: it swells a touch and fades evenly as a ghost
       while its sparks fly — "simply gone one frame later" when it shrank to
       a point on an eased-out fade (blind critics, 2026-09-25). */
    const spent = Object.assign({}, struck, {
      y: struck.y - size.height * 0.05,
      scale: struck.scale * 1.06,
      squash: 0,
      lift: 0.6,
      opacity: 0,
    });
    rememberCastPoint(dropData.instanceId, struck);
    removeCastRing();
    el.style.transition = 'none';
    /* The light breaks on the slam and blooms OVER the card — never an opaque
       white-out that erases what was cast. On the compositor it is made now
       and waits for the slam by its own delay; on the frame clock it is made
       as the slam lands. */
    const slamAt = CONSUME_WINDUP_MS + CONSUME_SLAM_MS;
    const path = [
      { pose: woundUp, duration: CONSUME_WINDUP_MS, easing: EASE_OUT, filter: 'brightness(1.35) saturate(1.25)' },
      { pose: struck, duration: CONSUME_SLAM_MS, easing: EASE_IN, filter: 'brightness(1.6) saturate(1.1)' },
      { pose: struck, duration: CONSUME_HOLD_MS, easing: EASE_OUT, filter: 'brightness(1.6) saturate(1.1)' },
      { pose: spent, duration: CONSUME_MS, easing: EASE_LINEAR, filter: 'brightness(1.9) saturate(0.9)' },
    ];
    const onCompositor = typeof el.animate === 'function';
    if (onCompositor) spawnCastFlash(struck, { delayMs: slamAt });
    tweenClonePath(el, pose, path, {
      onStop: onCompositor ? null : function (index) {
        if (index === 1 && interactionGeneration === generation) spawnCastFlash(struck);
      },
      done: finish,
    });
  }

  /**
   * A summon: the card glides over the slot it will land in and HANGS there —
   * the landing's own approach pose — while the server answers. The landing
   * (fx.js) claims it through `moonfall:summon-landing` and falls from exactly
   * this pose, so the drag and the slam are one motion with no swap between
   * them. A refusal flies it home; an acceptance that never lands (a Stellar
   * cinematic, an unanimated snapshot) lets it go gently.
   */
  function holdOverAnchor() {
    const cfg = hovered.cfg;
    const zoneKey = hovered.key;
    const dropData = Object.assign({}, data);
    const traceId = actionTraceId;
    let anchor = null;
    try { anchor = typeof cfg.anchor === 'function' ? cfg.anchor(dropData) : null; } catch (_) { anchor = null; }
    const holdPose = holdPoseFor(anchor) || Object.assign({}, pose, { scale: HOLD_SCALE, rotZ: 0 });
    traceRecord('drop-accepted', Object.assign({}, lastHitTestDebug || {}, { targetZone: cfg.zone || zoneKey }));

    // The clone and its source leave the interaction and belong to the HOLD:
    // the next press may start a new drag while this one is still in the air.
    releaseHeld('superseded');
    const holdGeneration = interactionGeneration;
    held = {
      instanceId: dropData.instanceId,
      el: clone,
      source: card,
      config: activeConfig,
      pose: Object.assign({}, pose),
      anchor,
      tween: null,
      accepted: false,
      acceptedAt: null,
      boardSeenAt: null,
      claimed: false,
      watch: null,
      watchTimer: null,
      anchorMarks: [],
      gliding: false,
      afterGlide: [],
      ghost: null,
      traceId,
      generation: holdGeneration,
    };
    const hold = held;
    hold.el.classList.add('card-drag-held');
    hold.el._dragShadow?.classList.add('card-drag-shadow--held');
    setPhase(hold.el, 'hold');
    hold.anchorMarks = markAll(anchor ? [anchor] : [], 'drop-zone-landing');
    tiltX = 0;
    tiltY = 0;
    /* THE GLIDE TO THE SLOT RUNS ON THE COMPOSITOR, and the page's own view of
       it can be stale: the server's answer arrives in the stall that follows
       a release, when the main thread still reports the card where it was let
       go even though the screen shows it arriving over the slot. Anything
       that needs the card's position — the landing taking it over, a refusal
       sending it home — therefore waits for the glide to END and reads the
       card where it has come to rest (whenSettled). Measured without this: the
       card reached its slot, jumped back to the release point and glided in
       a second time. */
    hold.gliding = true;
    hold.afterGlide = [];
    const glideDistance = Math.hypot(holdPose.x - hold.pose.x, holdPose.y - hold.pose.y);
    // Reduced motion: no travel — the card fades where it was let go while
    // its slot stays lit, and the landing announces it in the slot.
    const glideTo = reducedMotion() ? Object.assign({}, hold.pose, { opacity: 0 }) : holdPose;
    const glideMs = reducedMotion()
      ? 140
      : Math.round(clamp(HOLD_GLIDE_MIN_MS + glideDistance * HOLD_GLIDE_MS_PER_PX,
        HOLD_GLIDE_MIN_MS, HOLD_GLIDE_MAX_MS));
    let flight = null;
    const arc = reducedMotion() ? 0 : Math.min(glideDistance * HOLD_GLIDE_ARC, cardSize().height * 0.25);
    const afterGlide = function () {
      hold.pose = Object.assign({}, glideTo);
      hold.gliding = false;
      fadeHeldGhost(hold);
      hold.afterGlide.splice(0).forEach(function (fn) { fn(); });
      if (held === hold && !reducedMotion() && !flight) windUpHeld(hold);
    };
    // One active effect carries both beats: an overlapping delayed effect can
    // remain frozen while the main thread cannot activate its next phase.
    if (!reducedMotion()) flight = animateHeldFlight(hold, hold.pose, glideTo, glideMs, arc, afterGlide);
    hold.tween = flight || tweenClone(hold.el, hold.pose, glideTo, {
      duration: glideMs, easing: EASE_GLIDE, arc, done: afterGlide,
    });

    // The drag interaction is over; its classes and highlights go. The source
    // stays hidden (it is the card in the air) and the clone stays. The ghost
    // keeps the slot until the card arrives over it: gone at the release, the
    // slot "blinks empty just before the drop" (blind critic, 2026-09-25).
    hold.ghost = ghost;
    ghost = null;
    clone = null;
    card = null;
    validDrop = false;
    cleanup();

    const { result, dispatched } = dispatchZoneDrop(cfg, dropData, traceId, zoneKey);
    if (!dispatched) {
      returnHeld('not-dispatched');
      return;
    }
    Promise.resolve(result).then(function (outcome) {
      if (held !== hold) return;
      if (outcome && outcome.ok === false) {
        returnHeld(outcome.reason || 'refused');
        return;
      }
      hold.accepted = true;
      hold.acceptedAt = nowMs();
      watchHeld(hold);
    }, function () {
      if (held === hold) returnHeld('rejected');
    });
  }

  /* The card has come over its place: the ghost standing there gives way. */
  function fadeHeldGhost(hold) {
    const el = hold.ghost;
    hold.ghost = null;
    if (!el) return;
    el.classList.remove('card-drag-ghost--shown');
    scheduleDelayed(function () { el.remove(); }, 200);
  }

  function heldWindupPose(from) {
    return Object.assign({}, from, {
      scale: from.scale * HOLD_WINDUP_SCALE,
      y: from.y - cardSize().height * HOLD_WINDUP_RISE,
      lift: Math.max(from.lift || 0, LIFT_HELD) + 0.15,
    });
  }

  /* Split the existing glide's global cubic at its four quarter-arc poses.
     Each restricted cubic has its own normalized clock; together they trace
     exactly the same curve inside a longer, linearly timed native effect. */
  function heldGlideTiming() {
    const coordinate = function (a, b, t) {
      return 3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;
    };
    const derivative = function (a, b, t) {
      return 3 * (1 - t) * (1 - t) * a + 6 * (1 - t) * t * (b - a) + 3 * t * t * (1 - b);
    };
    const stops = [0, 0.25, 0.5, 0.75, 1].map(function (progress) {
      let low = 0;
      let high = 1;
      for (let i = 0; i < 48; i += 1) {
        const t = (low + high) / 2;
        if (coordinate(0, 1, t) < progress) low = t;
        else high = t;
      }
      const t = progress === 0 ? 0 : progress === 1 ? 1 : (low + high) / 2;
      return { progress, t, offset: coordinate(0.65, 0.35, t) };
    });
    return stops.map(function (stop, i) {
      const next = stops[i + 1];
      if (!next) return { offset: 1, easing: EASE_OUT.css };
      const dt = (next.t - stop.t) / 3;
      const dx = next.offset - stop.offset;
      const dy = next.progress - stop.progress;
      const controls = [
        dt * derivative(0.65, 0.35, stop.t) / dx,
        dt * derivative(0, 1, stop.t) / dy,
        1 - dt * derivative(0.65, 0.35, next.t) / dx,
        1 - dt * derivative(0, 1, next.t) / dy,
      ];
      return {
        offset: stop.offset,
        easing: `cubic-bezier(${controls.map(function (value) { return value.toFixed(9); }).join(', ')})`,
      };
    });
  }
  const HELD_GLIDE_TIMING = heldGlideTiming();

  function animateHeldFlight(hold, from, overSlot, glideMs, arc, afterGlide) {
    const el = hold.el;
    if (typeof el.animate !== 'function') return null;
    const to = heldWindupPose(overSlot);
    const duration = glideMs + HOLD_WINDUP_MS;
    const poses = [0, 0.25, 0.5, 0.75, 1].map(function (k) { return poseAt(from, overSlot, k, arc); });
    poses.push(to);
    const timing = HELD_GLIDE_TIMING.map(function (frame) {
      return { offset: frame.offset * glideMs / duration, easing: frame.easing };
    });
    timing.push({ offset: 1, easing: 'linear' });
    const shadow = el._dragShadow;
    let animation = null;
    let shadowAnimation = null;
    let glideTimer = null;
    let safety = null;
    let stopPlayback = null;
    let glided = false;
    let finished = false;
    const completeGlide = function () {
      if (finished || glided) return;
      glided = true;
      // A late main-thread callback must never put the CSS base back under a
      // windup the compositor has already painted. Only the logical gate ends.
      afterGlide();
    };
    const cancel = function () {
      if (finished) return;
      finished = true;
      stopPlayback?.();
      [glideTimer, safety].forEach(function (timer) {
        if (!timer) return;
        clearTimeout(timer);
        delayedTimers.delete(timer);
      });
      try { animation?.cancel(); } catch (_) { /* already gone */ }
      try { shadowAnimation?.cancel(); } catch (_) { /* already gone */ }
    };
    const finish = function () {
      if (finished) return;
      completeGlide();
      if (finished) return; // the authoritative landing may have claimed it
      if (held === hold) {
        applyPose(el, to);
        hold.pose = Object.assign({}, to);
      }
      cancel();
    };
    const checkGlide = function () {
      if (finished || glided) return;
      const current = animation.currentTime;
      if (typeof current !== 'number' || animation.startTime === null) return;
      const remaining = glideMs - current;
      if (remaining <= 0) completeGlide();
      else glideTimer = scheduleDelayed(checkGlide, Math.max(1, remaining));
    };
    const playbackReady = function () {
      if (finished) return;
      // Native playback starts after its first frame is ready, rather than at
      // animate() creation. Handoff and cleanup must follow that same clock.
      if (safety) {
        clearTimeout(safety);
        delayedTimers.delete(safety);
      }
      const current = animation.currentTime;
      safety = scheduleDelayed(finish, Math.max(0, duration - (typeof current === 'number' ? current : 0)) + 250);
      checkGlide();
    };
    applyPose(el, to);
    try {
      animation = el.animate(poses.map(function (p, i) {
        return Object.assign({
          transform: `translate(${(p.x - to.x).toFixed(2)}px, ${(p.y - to.y).toFixed(2)}px) ${cloneTransform(p, el)}`,
          opacity: p.opacity === undefined ? 1 : p.opacity,
        }, timing[i]);
      }), { duration, easing: 'linear' });
      animation.finished.then(finish, finish);
      if (typeof shadow?.animate === 'function') {
        shadowAnimation = shadow.animate(poses.map(function (p, i) {
          return Object.assign({ transform: shadowTransform(shadow, p), opacity: shadowOpacity(p) }, timing[i]);
        }), { duration, easing: 'linear' });
        shadowAnimation.finished.catch(function () { /* the handoff cancels both */ });
      }
      // Retain bounded cleanup if a hidden page never starts native playback.
      safety = scheduleDelayed(finish, duration + 250);
      animation.ready.then(playbackReady, function () { /* the handoff cancels pending playback */ });
      stopPlayback = followNativePlayback(el, animation, function (current) {
        if (current >= glideMs) completeGlide();
        if (!finished && current >= duration) finish();
      });
      return { cancel };
    } catch (_) {
      cancel();
      return null;
    }
  }

  function windUpHeld(hold) {
    const from = Object.assign({}, hold.pose);
    const to = heldWindupPose(from);
    hold.tween = tweenClone(hold.el, from, to, {
      duration: HOLD_WINDUP_MS,
      easing: EASE_OUT,
      done: function () {
        if (held === hold) hold.pose = Object.assign({}, to);
      },
    });
  }

  /** Let an accepted hold go if nothing claims it. */
  function watchHeld(hold) {
    const tick = function () {
      if (held !== hold) return;
      const now = nowMs();
      const escaped = window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape(hold.instanceId)
        : String(hold.instanceId).replace(/["\\]/g, '\\$&');
      const seated = /** @type {HTMLElement|null} */ (
        document.querySelector(`#player-board .card[data-instance-id="${escaped}"]`)
      );
      const artPending = !!seated && seated.classList.contains('card-summon-art-pending');
      const seatedVisible = seated && !artPending && !seated.dataset.enemyReplayArrival;
      if (seatedVisible && hold.boardSeenAt === null) hold.boardSeenAt = now;
      const waited = now - hold.acceptedAt;
      if ((hold.boardSeenAt !== null && now - hold.boardSeenAt > HOLD_CLAIM_GRACE_MS)
          || (!artPending && waited > HOLD_UNCLAIMED_MS)
          || waited > HOLD_PENDING_ART_MAX_MS) {
        fadeHeld('unclaimed');
        return;
      }
      hold.watch = requestAnimationFrame(tick);
    };
    hold.watch = requestAnimationFrame(tick);
    // The frame-driven watch is the rule; this timer only covers a page that
    // stopped drawing frames (a hidden tab).
    hold.watchTimer = scheduleDelayed(function () {
      if (held === hold) fadeHeld('unclaimed-timer');
    }, HOLD_PENDING_ART_MAX_MS + 400);
  }

  /** Run `fn` once the hold's glide to its slot has come to rest. */
  function whenSettled(hold, fn) {
    if (hold.gliding) hold.afterGlide.push(fn);
    else fn();
  }

  function endHeldState(hold) {
    releaseSeatedCopy(hold.instanceId);
    if (hold.ghost) {
      hold.ghost.remove();
      hold.ghost = null;
    }
    // Where the card IS, before its animation is dropped.
    hold.pose = livePose(hold.el, hold.pose);
    const queued = hold.gliding ? hold.afterGlide.splice(0) : [];
    hold.gliding = false;
    if (hold.tween) hold.tween.cancel();
    if (hold.watch) cancelAnimationFrame(hold.watch);
    if (hold.watchTimer) {
      clearTimeout(hold.watchTimer);
      delayedTimers.delete(hold.watchTimer);
    }
    clearMarks(hold.anchorMarks || [], 'drop-zone-landing');
    if (held === hold) held = null;
    // Anything still waiting on the glide learns the hold is over.
    queued.forEach(function (fn) { fn(); });
  }

  /** The landing took the card: it is on the board now, in person. */
  function claimHeld(detail) {
    const hold = held;
    if (!hold) return false;
    if (detail) detail.claimed = true;
    whenSettled(hold, function () {
      if (held !== hold) {
        // Released (a match reset) before it could land: the landing falls
        // back to its own entrance.
        if (detail && typeof detail.onReady === 'function') detail.onReady(null);
        return;
      }
      const rect = hold.el.getBoundingClientRect();
      const fromRect = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
      if (detail) detail.fromRect = fromRect;
      hold.claimed = true;
      const anchor = hold.anchor;
      endHeldState(hold);
      // The shadow stays under the falling card and closes onto the slot
      // with it: the height the card loses is read off its shadow.
      const shadow = hold.el._dragShadow;
      hold.el._dragShadow = null;
      if (shadow) {
        const slotRect = anchor && anchor.isConnected && typeof anchor.getBoundingClientRect === 'function'
          ? anchor.getBoundingClientRect()
          : null;
        const width = shadow._pw || 1;
        const seated = slotRect && slotRect.width > 0
          ? { x: slotRect.left + slotRect.width / 2, y: slotRect.top + slotRect.height / 2, scale: slotRect.width / width }
          : { x: hold.pose.x, y: hold.pose.y, scale: hold.pose.scale };
        /* UNDER the card that lands, never over it. At body level the shadow
           paints above the whole board, and closing onto the slot it passed
           over the landed card: "the card goes dark, then comes back" (blind
           critic, 2026-09-25). Seated in the slot — appended LAST, beneath the
           card by its negative z-index, never first, where the board looks for
           the card's own wrapper — it keeps the same viewport geometry: its
           origin is pinned to the viewport's (0, 0) through the slot's offset. */
        if (slotRect && slotRect.width > 0) {
          shadow.style.position = 'absolute';
          shadow.style.left = `${(-slotRect.left).toFixed(1)}px`;
          shadow.style.top = `${(-slotRect.top).toFixed(1)}px`;
          shadow.style.zIndex = '-1';
          anchor.appendChild(shadow);
        }
        settleShadow(shadow, Object.assign({ rotZ: 0, opacity: 1, lift: LIFT_HELD }, hold.pose),
          Object.assign(seated, { lift: 0, opacity: 0 }), HELD_SHADOW_FALL_MS);
      }
      hold.el.remove();
      traceFinishFor(hold.traceId, 'summon-landed', { claimed: true });
      // Same task as the removal: the real card takes over on the next frame.
      if (detail && typeof detail.onReady === 'function') detail.onReady(fromRect);
    });
    return true;
  }

  /* A summon whose clone was let go without a landing: its seated copy is
     already on screen, so a landing that arrives after all (a very late art
     settlement) must not drop it in a second time. */
  const settledWithoutLanding = new Map();
  const SETTLED_WITHOUT_LANDING_MS = 30000;
  // The landing's fall (fx.js HELD_FALL_MS): the shadow closes with it.
  const HELD_SHADOW_FALL_MS = 130;

  function fadeHeld(reason) {
    const hold = held;
    if (!hold) return;
    if (hold.gliding) {
      whenSettled(hold, function () { if (held === hold) fadeHeld(reason); });
      return;
    }
    if (reason !== 'no-landing') settledWithoutLanding.set(String(hold.instanceId), nowMs());
    endHeldState(hold);
    traceFinishFor(hold.traceId, 'summon-landed', { claimed: false, reason });
    const el = hold.el;
    tweenClone(el, hold.pose, Object.assign({}, hold.pose, { opacity: 0, lift: 0 }), {
      duration: 160,
      easing: EASE_OUT,
      done: function () { removeShadowOf(el); el.remove(); },
    });
  }

  /** The server said no: the card flies back to its seat. */
  function returnHeld(reason) {
    const hold = held;
    if (!hold) return;
    if (hold.gliding) {
      whenSettled(hold, function () { if (held === hold) returnHeld(reason); });
      return;
    }
    endHeldState(hold);
    traceFinishFor(hold.traceId, 'summon-refused', { reason });
    const source = liveSourceFor(hold.source, hold.instanceId);
    const el = hold.el;
    const home = source ? restingPoseOf(source) : null;
    if (!home) {
      if (source) revealSource(source);
      tweenClone(el, hold.pose, Object.assign({}, hold.pose, { opacity: 0, lift: 0 }), {
        duration: 160, done: function () { removeShadowOf(el); el.remove(); },
      });
      return;
    }
    tweenClone(el, hold.pose, Object.assign({}, home, { lift: 0 }), {
      duration: RETURN_MS,
      easing: EASE_BACK,
      arc: arcFor(hold.pose, home),
      done: function () {
        removeShadowOf(el);
        el.remove();
        const live = liveSourceFor(source, hold.instanceId);
        if (live) revealSource(live);
        markReturned(live);
      },
    });
  }

  /** A match reset or a newer hold ends this one without ceremony. */
  function releaseHeld(reason) {
    const hold = held;
    if (!hold) return;
    endHeldState(hold);
    traceFinishFor(hold.traceId, 'summon-hold-released', { reason });
    removeShadowOf(hold.el);
    hold.el.remove();
    const live = liveSourceFor(hold.source, hold.instanceId);
    if (live && live !== hold.source) revealSource(live);
    else if (hold.source && hold.source.isConnected) revealSource(hold.source);
  }

  function onSummonLanding(event) {
    const detail = event && event.detail;
    if (!detail) return;
    const settledAt = settledWithoutLanding.get(String(detail.instanceId));
    if (settledAt !== undefined) {
      settledWithoutLanding.delete(String(detail.instanceId));
      if (nowMs() - settledAt < SETTLED_WITHOUT_LANDING_MS) {
        // Already standing in its slot: no second arrival.
        detail.claimed = true;
        detail.skipLanding = true;
        if (typeof detail.onReady === 'function') detail.onReady('skip');
        return;
      }
    }
    if (!held || String(held.instanceId) !== String(detail.instanceId)) return;
    if (detail.noLanding) {
      fadeHeld('no-landing');
      detail.claimed = true;
      return;
    }
    claimHeld(detail);
  }
  window.addEventListener('moonfall:summon-landing', onSummonLanding);

  /* Home: the seat lights as the card lands in it — the landing's beat, the
     way a card taken back into the hand says it is back ("no landing light",
     blind critic, 2026-09-25). */
  const RETURNED_LIGHT_MS = 520;

  function markReturned(el) {
    const live = el && !el.isConnected && el.dataset ? liveSourceFor(el, el.dataset.instanceId) : el;
    if (!live || !live.classList || reducedMotion()) return;
    live.classList.add('card-drag-returned');
    setTimeout(function () { live.classList.remove('card-drag-returned'); }, RETURNED_LIGHT_MS);
  }

  function revealSource(el) {
    if (!el) return;
    el.classList.remove('dragging');
    el.style.visibility = '';
  }

  function liveSourceFor(el, instanceId) {
    if (el && el.isConnected) return el;
    return resolveLiveTrackedCard(el || { dataset: { instanceId } });
  }

  /**
   * Where `source` rests when nothing is holding it: its seat in the fan
   * (rotated about the bottom centre) or its board slot. Read with the drag's
   * own `dragging` class lifted for the measurement, because that class flattens
   * the transform the seat is drawn with.
   */
  function restingPoseOf(source) {
    if (!source || !source.isConnected) return null;
    const wasDragging = source.classList.contains('dragging');
    if (wasDragging) source.classList.remove('dragging');
    const measured = paintedPose(source);
    if (wasDragging) source.classList.add('dragging');
    if (!measured) return null;
    return Object.assign(measured, { opacity: 1, lift: 0 });
  }

  // --- Return home ---
  /**
   * The drop did nothing: the card flies back to its own seat and the seat
   * takes it back on the frame it arrives. A refused attempt says no FIRST,
   * where it was let go — the card shakes its head there and the owner's
   * feedback hook sounds the refusal — and only then goes home. Refused only
   * once it was back in the hand, the "no" arrived half a second late and
   * somewhere else: "nothing about the motion says no" (blind critic,
   * 2026-09-25).
   */
  function returnHome({ refused = null } = {}) {
    if (!clone || !card) {
      cleanup();
      return;
    }
    settling = 'return';
    const generation = interactionGeneration;
    const source = liveSourceFor(card, data && data.instanceId);
    const home = source ? restingPoseOf(source) : null;
    const config = activeConfig;
    const el = clone;
    el.style.transition = 'box-shadow 0.2s ease, filter 0.2s ease';
    el._tone = refused ? 'refused' : (playableLook && !refusal ? 'playable' : null);
    el.style.boxShadow = cloneGlow(el._tone, el._layoutScale);
    el.style.filter = '';
    tiltX = 0;
    tiltY = 0;
    // The reason stays up while the card says no, and goes as it flies home.
    if (!refused) dismissRefusalChip(el);
    if (refused && config && typeof config.onRefusedDrop === 'function') {
      try {
        config.onRefusedDrop({ element: source, refusal: refused });
      } catch (_) { /* feedback is presentation only */ }
    }
    const finish = function () {
      if (interactionGeneration !== generation) return;
      cleanup();
    };
    const flyHome = function () {
      if (interactionGeneration !== generation) return;
      el.classList.remove('card-drag-clone--refusing');
      dismissRefusalChip(el);
      setPhase(el, 'return');
      if (!home || reducedMotion()) {
        tweenClone(el, pose, Object.assign({}, pose, { opacity: 0, lift: 0 }), {
          duration: home ? 120 : 180, done: finish,
        });
        return;
      }
      tweenClone(el, pose, Object.assign({}, home, { lift: 0 }), {
        duration: RETURN_MS,
        easing: EASE_BACK,
        arc: arcFor(pose, home),
        done: function () {
          finish();
          markReturned(source);
        },
      });
    };
    if (refused && home && !reducedMotion()) {
      setPhase(el, 'refuse');
      // A red flash on the card itself: the play is refused THERE.
      el.classList.add('card-drag-clone--refusing');
      shakeClone(el, pose, { duration: REFUSE_SHAKE_MS, done: flyHome });
      return;
    }
    flyHome();
  }

  /* The head-shake of a refused release: side to side about where the card
     was let go, dying away, going nowhere. */
  const REFUSE_SHAKE_OFFSETS = [0, 1, -1, 0.6, -0.3, 0];
  const REFUSE_SHAKE_AT = [0, 0.16, 0.4, 0.62, 0.82, 1];

  function refuseShakeOffset(t) {
    for (let i = 1; i < REFUSE_SHAKE_AT.length; i += 1) {
      if (t <= REFUSE_SHAKE_AT[i]) {
        const span = REFUSE_SHAKE_AT[i] - REFUSE_SHAKE_AT[i - 1];
        const k = span > 0 ? (t - REFUSE_SHAKE_AT[i - 1]) / span : 1;
        return lerp(REFUSE_SHAKE_OFFSETS[i - 1], REFUSE_SHAKE_OFFSETS[i], easeInOutQuad(k));
      }
    }
    return 0;
  }

  function shakeClone(el, p, { duration, done }) {
    const amplitude = uiPx(REFUSE_SHAKE_PX);
    const shadow = el._dragShadow || null;
    let finished = false;
    const finish = function () {
      if (finished) return;
      finished = true;
      applyPose(el, p);
      done();
    };
    applyPose(el, p);
    if (typeof el.animate === 'function') {
      try {
        const frames = REFUSE_SHAKE_OFFSETS.map(function (k, i) {
          return {
            transform: `translate(${(k * amplitude).toFixed(2)}px, 0px) ${cloneTransform(p, el)}`,
            offset: REFUSE_SHAKE_AT[i],
            easing: 'ease-in-out',
          };
        });
        const animation = el.animate(frames, { duration });
        if (shadow && typeof shadow.animate === 'function') {
          shadow.animate(REFUSE_SHAKE_OFFSETS.map(function (k, i) {
            return {
              transform: shadowTransform(shadow, Object.assign({}, p, { x: p.x + k * amplitude })),
              offset: REFUSE_SHAKE_AT[i],
              easing: 'ease-in-out',
            };
          }), { duration });
        }
        animation.finished.then(finish, function () { /* cancelled: the caller took over */ });
        scheduleDelayed(finish, duration + 250);
        return;
      } catch (_) { /* the frame clock shakes it instead */ }
    }
    let startedAt = null;
    const step = function (frameTime) {
      if (finished) return;
      const now = Number.isFinite(frameTime) ? frameTime : nowMs();
      if (startedAt === null) startedAt = now;
      const t = clamp((now - startedAt) / duration, 0, 1);
      applyPose(el, Object.assign({}, p, { x: p.x + refuseShakeOffset(t) * amplitude }));
      if (t >= 1) finish();
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    scheduleDelayed(finish, duration + 120);
  }

  // --- Cancel ---
  function cancel() {
    if (!dragging) return;
    dragging = false;
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    traceFinish('drag-cancelled', { reason: 'cancelled' });
    updateHover(null);
    if (castRing) castRing.classList.remove('card-drag-cast-ring--shown');
    if (ghost) ghost.classList.remove('card-drag-ghost--shown');
    removeTether();
    returnHome();
  }

  // --- Cleanup ---
  function cleanup() {
    var dropWasValid = validDrop;
    removeGhost();
    removeCastRing();
    removeTether();
    if (clone) {
      removeShadowOf(clone);
      if (clone.parentNode) {
        clone.parentNode.removeChild(clone);
      }
    }
    // Belt-and-suspenders: when no drag is active, remove any stray clones too
    // (orphans from an earlier skipped cleanup). Guarded by !dragging so we never
    // remove the live clone of a drag that started during this cleanup's timer,
    // and never a summon still held over its landing slot.
    if (!dragging) {
      var strays = document.querySelectorAll('.card-drag-clone:not(.card-drag-held), .card-drag-shadow:not(.card-drag-shadow--held)');
      if (strays.length) {
        console.warn('[CardDrag] cleanup removed ' + strays.length + ' stray drag clone(s).');
        strays.forEach(function (el) { el.remove(); });
      }
    }
    updateHover(null);
    availableMarks = clearAvailableMarks();

    // Suppress transitions on ALL cards before removing drag classes, so no
    // card animates out of whatever the drag suspended.
    var allCards = document.querySelectorAll('.card');
    allCards.forEach(function (c) {
      if (c.classList.contains('card-drag-clone')) return;
      c.style.transition = 'none';
      c.style.zoom = '';
    });

    latestHandPlay = null;
    if (card) {
      card.classList.remove('dragging');
      if (data) dropCarriedLook(data.instanceId, data.sourceType);
      if (validDrop && data && data.sourceType === 'hand') {
        // Played from the hand: it leaves the hand (see beginPendingHandPlay).
        beginPendingHandPlay(liveSourceFor(card, data.instanceId) || card, data.instanceId, activeConfig);
      } else if (validDrop) {
        // Valid drop: show card dimmed while server processes.
        // The action-in-progress CSS disables pointer-events so it's non-interactive.
        // The dim is OURS to end — see applyPendingActionDim; the board no
        // longer rebuilds the node out from under it. On the node that is
        // actually on the board: a render during the drop may have replaced it.
        const live = liveSourceFor(card, data && data.instanceId) || card;
        live.style.visibility = '';
        applyPendingActionDim(live, data && data.instanceId);
      } else {
        // Spring-back: restore source card so it reappears on the board/hand —
        // the node a re-render may have put in its place, too.
        const live = liveSourceFor(card, data && data.instanceId);
        if (live && live !== card) {
          live.classList.remove('dragging');
          live.style.visibility = '';
        }
        card.style.visibility = '';
        // Drop the bookkeeping with the pixels, so a later settlement cannot
        // believe it still owns a card that is already back at rest.
        if (pendingActionDim && pendingActionDim.element === card) {
          pendingActionDim = null;
        }
        card.style.opacity = '';
      }
    }

    // Clear GameState
    if (typeof GameState !== 'undefined' && GameState.set) {
      GameState.set('draggedCard', null);
      GameState.set('draggedCardIndex', null);
      GameState.set('dragSourceType', null);
      GameState.set('dragSourceZone', null);
    }

    // Clear highlights
    HighlightUtil.clearPotentialTargetHighlights();

    // Remove body classes
    document.body.classList.remove('card-dragging', 'card-dragging--zone', 'card-dragging--target');

    // For valid drops, add a short-lived settling class.  renderBoard()
    // recreates board slots under the cursor, which fires mouseenter →
    // animateZoom.  The settling class lets zoom handlers skip the event
    // until the board has fully re-rendered.
    if (dropWasValid) {
      document.body.classList.add('drop-settling');
      scheduleDelayed(function () {
        document.body.classList.remove('drop-settling');
        window.dispatchEvent(new CustomEvent('moonfall:drop-settling-ended', {
          detail: { pointer: { x: Math.round(curX), y: Math.round(curY) } }
        }));
      }, 500);
    }

    // Force reflow then restore transitions so cards settle instantly
    document.body.offsetHeight; // eslint-disable-line no-unused-expressions
    allCards.forEach(function (c) {
      if (c.classList.contains('card-drag-clone')) return;
      c.style.transition = '';
    });

    // Restore hover overlay to its default inactive state.
    // The overlay is initialised with pointer-events:none (inactive).
    // Setting it to '' would remove the inline value and default to 'auto',
    // making the full-screen z-5000 overlay eat all subsequent pointer events.
    var overlay = document.getElementById('hover-overlay');
    if (overlay) {
      overlay.style.display = '';
      overlay.style.pointerEvents = 'none';
    }

    // Null state
    clone = null;
    card = null;
    data = null;
    activeConfig = null;
    origRect = null;
    hovered = null;
    hoveredTarget = null;
    refusal = null;
    validDrop = false;
    settling = null;
    vx = 0;
    vy = 0;
    tiltX = 0;
    tiltY = 0;
    pose = null;
    lifting = false;
    liftFrom = null;
    liftStartedAt = null;
    liftElapsed = 0;
    overHome = false;
    leftHome = false;
    carryOffset = null;
    rising = false;
    seatPose = null;
    magnetVel = null;
    liftKickPending = false;
    speedX = 0;
    lean = 0;
    leanVelocity = 0;
    lastFrameAt = null;
    rafId = null;
    actionTraceId = null;
    lastHitTestDebug = null;
  }

  function abandonInteraction(reason) {
    var abandonedPointerId = trackingPointerId;
    var acceptedDrag = dragging;
    interactionGeneration += 1;
    clearDelayedTimers();
    if (rafId) cancelAnimationFrame(rafId);
    if (actionTraceId) {
      traceFinish('drag-cancelled', { reason: reason || 'interaction-reset' });
    }
    dragging = false;
    trackingCard = null;
    trackingConfig = null;
    trackingPointerId = null;
    trackingPreviewPose = null;
    if (abandonedPointerId !== null) {
      settleTouchPreviewHandoff(abandonedPointerId, acceptedDrag);
    }
    validDrop = false;
    cleanup();
    /* `legal-play` is deliberately NOT cleared here. It is the affordability
       affordance the hand render owns — the green glow on every card the player
       can currently pay for — not drag state, and nothing suppresses it while a
       drag is in flight. Clearing it meant the first drag of a turn, completed
       or cancelled, darkened every affordable card in hand until the next
       server update re-rendered it. Reported as "I have summonable cards but
       they don't have the green hue".
       Locked by tests/unit/ui/cardDrag.legalPlayGlow.test.js. */
    targets.forEach(function (_config, target) {
      target?.classList?.remove('valid-target', 'potential-target');
    });
    document.querySelectorAll('.card-drag-clone:not(.card-drag-held), .card-drag-shadow:not(.card-drag-shadow--held), .card-drag-ghost, .card-drag-cast-ring, .card-drag-tether').forEach(function (element) { element.remove(); });
    document.querySelectorAll('.dragging, .valid-target, .potential-target, .drop-zone-available, .drop-zone-armed').forEach(function (element) {
      if (held && element === held.source) return;
      element.classList.remove('dragging', 'valid-target', 'potential-target', 'drop-zone-available', 'drop-zone-armed');
    });
    /* A drag is exactly when affordability may have changed (a card was played,
       mana was spent) — and it is also when the hand's DOM was churned. Rather
       than leaving the glow to whatever the last render decided, re-derive it
       from current state. Idempotent and cheap. */
    Cards?.refreshLegalPlayHighlights?.();
    document.body.classList.remove('card-dragging', 'card-dragging--zone', 'card-dragging--target', 'drop-settling');
  }

  function reset() {
    abandonInteraction('match-reset');
    releaseHeld('match-reset');
    releaseAllPendingHandPlays();
    // A new match inherits nothing. (A mere interaction abandon — a blur, a
    // second finger — deliberately does NOT release: the action it announces
    // is still in flight with the server.)
    releasePendingActionDim(pendingActionDim, 'match-reset');
    clearTargets();
  }

  // --- Gesture ownership ---
  /**
   * The gesture belongs to this module and a pointerId, NOT to the source
   * element. Moonfall re-renders the hand and the board on every server state
   * update, so the element under the finger is routinely detached mid-gesture;
   * when the gesture lived on that element's own listeners, its pointerup was
   * never delivered and `trackingCard` stayed set forever, refusing every later
   * press. Tracking on document survives any re-render: captured events still
   * bubble here, and once implicit capture is released by the detach the events
   * land here on their own.
   */
  /**
   * Find the element that IS this card now, after a re-render replaced it.
   * Identity comes from the instance id, which survives the swap; the element
   * reference does not.
   */
  function resolveLiveTrackedCard(detached) {
    try {
      var id = detached && detached.dataset ? detached.dataset.instanceId : null;
      if (!id || typeof document.querySelector !== 'function') return null;
      var escaped = (window.CSS && typeof window.CSS.escape === 'function')
        ? window.CSS.escape(id)
        : String(id).replace(/["\\]/g, '\\$&');
      var matches = document.querySelectorAll('.card[data-instance-id="' + escaped + '"]');
      for (var i = 0; i < matches.length; i++) {
        var live = matches[i];
        if (live.isConnected && !live.classList.contains('card-drag-clone')) return live;
      }
      return null;
    } catch (_) {
      return null;
    }
  }

  function endTracking(eventName, reason) {
    if (!trackingCard) return;
    var releasedPointerId = trackingPointerId;
    trackingCard = null;
    trackingConfig = null;
    trackingPointerId = null;
    trackingPreviewPose = null;
    if (!dragging) {
      settleTouchPreviewHandoff(releasedPointerId, false);
      traceFinish(eventName, { reason: reason });
      actionTraceId = null;
    }
  }

  /** Tracking whose source card vanished with a re-render is dead weight. */
  function dropStaleTracking() {
    if (!trackingCard || dragging) return;
    if (!trackingCard.isConnected) endTracking('pointer-source-detached', 'source-removed');
  }

  /**
   * Begin tracking a press on `cardEl`. Shared by the card's own pointerdown
   * and by a press on the card's enlarged inspection popup.
   */
  function startTracking(cardEl, config, e, { previewPose = null } = {}) {
    if (e.button !== 0 && e.button !== undefined) return false;
    /* Only the first finger can carry a card. A second one is a pinch: the
       document guard ends the first finger's press, and the same event then
       reaching the card must not hand the gesture to the second finger. */
    if (e.pointerType === 'touch' && e.isPrimary === false) return false;
    dropStaleTracking();
    if (dragging || trackingCard) {
      MoonfallActionTrace?.instant?.(config.instanceId, 'pointerdown-blocked', {
        reason: dragging ? 'drag-already-active' : 'pointer-already-tracked',
      });
      return false;
    }
    if (config.canDrag && !config.canDrag()) {
      MoonfallActionTrace?.instant?.(config.instanceId, 'pointerdown-blocked', {
        reason: 'can-drag-rejected',
        sourceZone: config.sourceZone,
      });
      return false;
    }

    // A prior valid/rejected drop can still be in its visual fade while no
    // pointer is active. Finish that stale visual state before accepting the
    // next press; generation guards make its delayed timer harmless.
    if (!dragging && !trackingCard && (clone || card || data)) cleanup();
    interactionGeneration += 1;
    e.preventDefault();
    actionTraceId = MoonfallActionTrace?.start?.(config.instanceId, {
      sourceZone: config.sourceZone,
      sourceType: config.sourceType || null,
      pointer: { x: Math.round(e.clientX), y: Math.round(e.clientY) },
      pointerType: e.pointerType || 'mouse',
    }) || null;
    trackingCard = cardEl;
    trackingConfig = config;
    trackingPointerId = e.pointerId;
    trackingPointerType = e.pointerType || 'mouse';
    trackingSlop = e.pointerType === 'touch' ? TOUCH_THRESHOLD : uiPx(THRESHOLD);
    if (previewPose) trackingSlop = Math.max(trackingSlop, INSPECTED_CARD_THRESHOLD);
    trackingPreviewPose = previewPose;
    startX = e.clientX;
    startY = e.clientY;
    curX = startX;
    curY = startY;
    /* Capture keeps a mouse released outside the window from stranding the
       drag. It is deliberately NOT what the gesture depends on: losing it
       (the browser releases capture when the captor leaves the document) is
       no longer a cancellation, because document tracking carries on.
       A press on the enlarged card is never captured: it landed on whatever
       lies beneath the pointer-transparent popup, and capturing it onto the
       hand card moved the pointer's target — the boundary `pointerleave` that
       follows reads to CardTilt's popup lean as the finger lifting, and the
       enlarged card stopped leaning under a finger still on it. */
    if (!previewPose) {
      try { cardEl.setPointerCapture(e.pointerId); } catch (_) { /* detached */ }
    }
    return true;
  }

  /**
   * A press ON the enlarged inspection popup is a press on the card it shows.
   *
   * The battle popup is pointer-transparent (it tilts under the finger through
   * CardTilt's document listener), so a press on it lands on whatever is
   * painted BENEATH — a board card, an empty slot, the table. Starting a drag
   * there picked up the wrong card, or nothing, exactly when the player had
   * just read a card and reached for it: the one ambiguity the brief forbids.
   * The inspected card owns every press that lands on its enlargement.
   */
  function pressOnInspectedCard(e) {
    /** @type {any} */
    let source = null;
    try { source = TouchPreview?.latchedElement?.() || null; } catch (_) { source = null; }
    source = liveInspectedSource(source);
    if (!source) return null;
    const preview = document.querySelector('#card-hover-preview .card');
    if (!preview || typeof preview.getBoundingClientRect !== 'function') return null;
    const rect = preview.getBoundingClientRect();
    const inside = e.clientX >= rect.left && e.clientX <= rect.right
      && e.clientY >= rect.top && e.clientY <= rect.bottom;
    if (!inside) return null;
    return { source, previewPose: paintedPose(preview) };
  }

  /* The card the popup shows, as it is on the page NOW. An authoritative
     snapshot can rebuild the hand while a card is enlarged: the popup stays
     up, but the element the tap latched is gone, and a drag off the
     enlargement did nothing at all. The redrawn node for the same card, in
     the same zone, is the one to pick up. */
  function liveInspectedSource(latched) {
    if (!latched) return null;
    if (latched.isConnected) return latched._cardDragConfig ? latched : null;
    const instanceId = latched.dataset && latched.dataset.instanceId;
    const zone = latched._cardDragConfig && latched._cardDragConfig.sourceZone;
    if (!instanceId) return null;
    const escaped = window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(String(instanceId))
      : String(instanceId).replace(/["\\]/g, '\\$&');
    const nodes = document.querySelectorAll(`.card[data-instance-id="${escaped}"]`);
    for (const node of nodes) {
      /** @type {any} */
      const candidate = node;
      if (!candidate._cardDragConfig || candidate.classList.contains('card-drag-clone')) continue;
      if (candidate.closest('#card-hover-preview')) continue;
      if (zone && candidate._cardDragConfig.sourceZone !== zone) continue;
      return candidate;
    }
    return null;
  }

  function onDocumentPointerDown(e) {
    /* Browser chrome can consume the terminal event while leaving a connected
       source tracked. The next real primary touch begins a new gesture even if
       iOS reused its pointer id and even if it lands on a non-draggable control.
       Capture at document scope so target eligibility cannot block recovery. */
    if (e.pointerType === 'touch' && e.isPrimary === true && trackingCard) {
      abandonInteraction('stale-pointer-before-new-primary');
      return;
    }
    /* A second finger is a pinch — the phone's card magnifier (zoom-policy.js)
       — and never part of a drag. The browser cancels both pointers once it
       takes the pinch, but not every engine does so before the first finger
       has moved, and a drag still running under a zoom could DROP the card
       on the board. End it here; a lifted card goes home. */
    if (e.pointerType === 'touch' && e.isPrimary === false && trackingCard) {
      var wasDragging = dragging;
      endTracking('pointer-cancelled', 'second-touch-pinch');
      if (wasDragging) cancel();
      return;
    }
    if (!trackingCard && !dragging && (e.button === 0 || e.button === undefined)) {
      const inspected = pressOnInspectedCard(e);
      if (inspected
          && startTracking(inspected.source, inspected.source._cardDragConfig, e, {
            previewPose: inspected.previewPose,
          })) {
        // The press is the inspected card's; nothing painted beneath the
        // popup may start its own gesture from it.
        e.stopPropagation();
      }
    }
  }

  function onDocumentPointerMove(e) {
    if (!trackingCard || e.pointerId !== trackingPointerId) return;
    curX = e.clientX;
    curY = e.clientY;
    if (dragging) return;

    var dx = curX - startX;
    var dy = curY - startY;
    if (Math.sqrt(dx * dx + dy * dy) < trackingSlop) return;
    if (!trackingCard.isConnected) {
      /* The card was REPLACED between press and threshold — which happens
         routinely: inspecting a board card prepares its enlarged preview, and
         that re-raster can swap the element. Aborting here made the very next
         drag do nothing at all, silently, with no clone: a card the player had
         just looked at became undraggable.
         The element is not the identity — the card is. Re-resolve the live
         node by instance id and carry on; only give up if the card is really
         gone from the DOM. */
      var live = resolveLiveTrackedCard(trackingCard);
      if (!live) {
        endTracking('pointer-source-detached', 'source-removed-before-threshold');
        return;
      }
      trackingCard = live;
      const liveConfig = /** @type {any} */ (live)._cardDragConfig;
      if (liveConfig) trackingConfig = liveConfig;
    }
    beginDrag(trackingCard, trackingConfig);
  }

  function onDocumentPointerUp(e) {
    if (!trackingCard || e.pointerId !== trackingPointerId) return;
    curX = e.clientX;
    curY = e.clientY;
    traceRecord('pointerup', {
      pointer: { x: Math.round(curX), y: Math.round(curY) },
      dragging: dragging,
    });
    var wasDragging = dragging;
    endTracking('pointer-released-without-drag', 'threshold-not-crossed');
    if (wasDragging) drop();
  }

  function onDocumentPointerCancel(e) {
    if (!trackingCard || e.pointerId !== trackingPointerId) return;
    var wasDragging = dragging;
    endTracking('pointer-cancelled', 'pointercancel-before-threshold');
    if (wasDragging) cancel();
  }

  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('pointermove', onDocumentPointerMove, true);
  document.addEventListener('pointerup', onDocumentPointerUp, true);
  document.addEventListener('pointercancel', onDocumentPointerCancel, true);

  // --- Make draggable ---
  /**
   * `config`: `instanceId`, `cardId`, `sourceZone`, `sourceType`, and
   *  - `dropMode`: 'target' (registered elements) or 'zone' (registered zones);
   *  - `canDrag()`: false when this card cannot even be lifted;
   *  - `refusal()`: null, or `{ reason, message }` when it lifts but cannot be
   *    played now — the message is shown on the lifted card;
   *  - `onRefusedDrop({ element, refusal })`: a refused card was let go where
   *    it would have played;
   *  - `homeContains(pointer)`: true over the card's own home (its hand), where
   *    the carried card settles back to its resting size.
   *
   * @param {any} cardEl
   * @param {any} config
   */
  function makeDraggable(cardEl, config) {
    var handlers = {};

    handlers.pointerdown = function (e) {
      startTracking(cardEl, config, e);
    };

    cardEl.addEventListener('pointerdown', handlers.pointerdown);

    cardEl.setAttribute('draggable', 'false');
    cardEl.style.touchAction = 'none';

    // Suppress any native drag ghost
    handlers.dragstart = function (e) { e.preventDefault(); };
    cardEl.addEventListener('dragstart', handlers.dragstart);

    cardEl._cardDragHandlers = handlers;
    cardEl._cardDragConfig = config;
  }

  // --- Cleanup draggable ---
  /**
   * Detaches the listeners that START a gesture. It deliberately does NOT touch
   * a gesture already in flight: renderBoard() calls this on every card on
   * every server state update, and cancelling there aborted drags mid-motion
   * whenever the opponent acted. An in-flight gesture is owned by its pointer
   * and ends with that pointer.
   */
  function cleanupDraggable(cardEl) {
    var handlers = cardEl._cardDragHandlers;
    if (!handlers) return;
    cardEl.removeEventListener('pointerdown', handlers.pointerdown);
    if (handlers.dragstart) {
      cardEl.removeEventListener('dragstart', handlers.dragstart);
    }
    delete cardEl._cardDragHandlers;
    delete cardEl._cardDragConfig;
  }

  /**
   * Is this card instance in the air right now — lifted, flying home, or held
   * over its landing slot? A renderer rebuilding the hand or board asks, so the
   * rebuilt node does not appear in its seat while the card is still flying.
   */
  function isCarrying(instanceId, zone) {
    if (instanceId === undefined || instanceId === null) return false;
    const id = String(instanceId);
    // A held summon is the hand card in the air; its seated board copy is
    // governed by isHeldSummon, never hidden as a "carried" board card.
    if (held && String(held.instanceId) === id) return zone !== 'board';
    // Being spent (a cast's wind-up, slam and dissolve) is still in flight:
    // a render that seats it again puts it under its own dissolve.
    if (!data || String(data.instanceId) !== id
        || !(dragging || settling === 'return' || settling === 'consume')) return false;
    if (!zone) return true;
    return (zone === 'board') === (data.sourceType === 'board');
  }

  /** A summon hanging over its landing slot, waiting for the landing to take it. */
  function isHeldSummon(instanceId) {
    return !!held && instanceId !== undefined && instanceId !== null
      && String(held.instanceId) === String(instanceId);
  }

  /** The seated board copy of a held summon may paint again. */
  function releaseSeatedCopy(instanceId) {
    try {
      const escaped = window.CSS && typeof window.CSS.escape === 'function'
        ? window.CSS.escape(String(instanceId))
        : String(instanceId).replace(/["\\]/g, '\\$&');
      document.querySelectorAll(`#player-board .card[data-instance-id="${escaped}"]`).forEach(function (el) {
        delete /** @type {HTMLElement} */ (el).dataset.dragHeldArrival;
      });
    } catch (_) { /* presentation only */ }
  }

  // --- Escape key ---
  function onDocumentKeyDown(e) {
    if (e.key === 'Escape' && dragging) {
      cancel();
    }
  }
  document.addEventListener('keydown', onDocumentKeyDown);

  function onLifecycleLoss() {
    abandonInteraction('document-lifecycle-loss');
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') onLifecycleLoss();
  }

  window.addEventListener('blur', onLifecycleLoss);
  window.addEventListener('pagehide', onLifecycleLoss);
  document.addEventListener('visibilitychange', onVisibilityChange);

  /** Detach every document listener this instance owns (see the guard above). */
  function destroy() {
    reset();
    document.removeEventListener('pointerdown', onDocumentPointerDown, true);
    document.removeEventListener('pointermove', onDocumentPointerMove, true);
    document.removeEventListener('pointerup', onDocumentPointerUp, true);
    document.removeEventListener('pointercancel', onDocumentPointerCancel, true);
    document.removeEventListener('keydown', onDocumentKeyDown);
    window.removeEventListener('moonfall:action-intent-settled', onActionIntentSettled);
    window.removeEventListener('moonfall:summon-landing', onSummonLanding);
    window.removeEventListener('blur', onLifecycleLoss);
    window.removeEventListener('pagehide', onLifecycleLoss);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  // --- Public API ---
  return {
    destroy: destroy,
    registerTarget: registerTarget,
    unregisterTarget: unregisterTarget,
    clearTargets: clearTargets,
    registerZone: registerZone,
    unregisterZone: unregisterZone,
    makeDraggable: makeDraggable,
    cleanupDraggable: cleanupDraggable,
    reset: reset,
    isDragging: function () { return dragging; },
    isCarrying: isCarrying,
    isHeldSummon: isHeldSummon,
    isPlayPending: isPlayPending,
    castPointOf: castPointOf,
    ownsPointer: function (pointerId) {
      return !!trackingCard
        && (pointerId === undefined || pointerId === trackingPointerId);
    },
    // Published so the tap-to-enlarge latch can use the SAME slop: if the two
    // disagreed, movement in the gap would cancel the tap without starting a
    // drag and the gesture would silently do nothing.
    touchSlopPx: function () { return TOUCH_THRESHOLD; },
    getDragData: function () { return dragging ? data : null; }
  };
})();

export { CardDrag };
